// i18n solution supporting English and Polish with automatic locale detection
// and manual language selection.

import { pl as dateFnsPl, enUS as dateFnsEn } from "date-fns/locale";

export type Locale = "pl" | "en";

const LOCALE_STORAGE_KEY = "parrot-locale";

// Detect locale from browser - Polish only if the browser is in Polish
function detectBrowserLocale(): Locale {
  if (typeof navigator === "undefined") {
    return "en"; // Server-side default
  }

  const browserLang = navigator.language || navigator.languages?.[0] || "en";
  return browserLang.toLowerCase().startsWith("pl") ? "pl" : "en";
}

// Get saved locale from localStorage or detect from browser
export function getLocale(): Locale {
  if (typeof localStorage === "undefined") {
    return detectBrowserLocale();
  }

  const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
  if (saved === "pl" || saved === "en") {
    return saved;
  }

  return detectBrowserLocale();
}

// Set locale and persist to localStorage
export function setLocale(locale: Locale): void {
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  }
}

// Get the current locale - can be called reactively
let currentLocale: Locale | null = null;

function getCurrentLocale(): Locale {
  if (currentLocale === null) {
    currentLocale = getLocale();
  }
  return currentLocale;
}

// Update the current locale (call this when user changes language)
export function updateCurrentLocale(locale: Locale): void {
  currentLocale = locale;
  setLocale(locale);
}

// Get date-fns locale based on current locale
export function getDateFnsLocale() {
  return getCurrentLocale() === "pl" ? dateFnsPl : dateFnsEn;
}

export function t(key: keyof typeof pl, params: Record<string, string> = {}) {
  const locale = getCurrentLocale();
  const translations = locale === "pl" ? pl : en;
  let str = translations[key] ?? key;

  // Replace placeholders with actual values from params
  for (const param in params) {
    str = str.replace(`{{${param}}}`, params[param]);
  }

  return str;
}

// Get a translation function for a specific locale (useful for landing pages with forced locale)
export function getTranslationFn(locale: Locale) {
  return (key: keyof typeof pl, params: Record<string, string> = {}) => {
    const translations = locale === "pl" ? pl : en;
    let str = translations[key] ?? key;

    for (const param in params) {
      str = str.replace(`{{${param}}}`, params[param]);
    }

    return str;
  };
}

// English translations
const en = {
  // I use it to not lose typing when calling t() function but also to be able to call it on arbitrary validation errors
  __generic: "__generic",

  "Hello!": "Hello!",
  Save: "Save",
  Delete: "Delete",
  Back: "Back",
  None: "None",
  Dismiss: "Dismiss",

  // Auth
  Logout: "Logout",
  "Log in": "Log in",

  // Projects
  "Add project": "Add project",
  "New project": "New project",
  "Delete project": "Delete project",
  "Project name": "Project name",
  Projects: "Projects",
  "Search projects...": "Search projects...",
  "No projects found": "No projects found",
  Questions: "Questions",
  Ask: "Ask",
  Question: "Question",
  Answer: "Answer",
  "Project with that name already exists":
    "Project with that name already exists",
  "Name should have at least 3 letters": "Name should have at least 3 letters",
  "Name cannot be empty": "Name cannot be empty",
  "Processing data...": "Processing data...",
  source: "source",
  "Are you sure you want to delete this project?":
    "Are you sure you want to delete this project?",
  "Rename project": "Rename project",
  "Private project": "Private project",
  "Project description": "Project description",
  "Add a description...": "Add a description...",
  "Start a new conversation...": "Start a new conversation...",

  // Documents
  "Attach documents": "Attach documents",
  "Delete document": "Delete document",
  "Document removed": "Document removed",
  "The document has been removed from the upload queue":
    "The document has been removed from the upload queue",
  "Document deleted": "Document deleted",
  "The document has been deleted successfully":
    "The document has been deleted successfully",
  "Failed to delete document": "Failed to delete document",
  "An error occurred while deleting the document":
    "An error occurred while deleting the document",
  Documents: "Documents",
  "Upload documents": "Upload documents",
  "Uploading...": "Uploading...",
  "Processing...": "Processing...",
  "Uploading {{file}} failed": "Uploading {{file}} failed",
  "Uploaded {{time}}": "Uploaded {{time}}",
  "File too big": "File too big",
  "{{file}} is too big. Max file size is {{size}}.":
    "{{file}} is too big. Max file size is {{size}}.",
  "File already exists": "File already exists",
  "{{file}} already exists": "{{file}} already exists",
  "Invalid file type": "Invalid file type",
  "{{file}} is not a supported file type.":
    "{{file}} is not a supported file type.",
  "Drop files here to upload": "Drop files here to upload",
  "File processing failed. Remove the file and try again.":
    "File processing failed. Remove the file and try again.",
  Error: "Error",
  "Failed to update document selection. Please try again.":
    "Failed to update document selection. Please try again.",
  "Document not found": "Document not found",
  "Rename document": "Rename document",
  "No documents yet": "No documents yet",
  "Upload your first document to get started":
    "Upload your first document to get started",
  "Document name": "Document name",
  "{{file}} is not a supported file type":
    "{{file}} is not a supported file type",
  "Document extension mismatch": "Document extension mismatch",

  // Topics
  Topics: "Topics",
  Messages: "Messages",
  "Recent topics": "Recent topics",
  "New topic": "New topic",
  "New topic name": "New topic name",
  "Created {{time}}": "Created {{time}}",
  "Delete topic": "Delete topic",
  Untitled: "Untitled",
  "Topic name": "Topic name",
  "Rename topic": "Rename topic",

  // Chat related
  "Thank you for your feedback!": "Thank you for your feedback!",
  "We'll use this to improve our responses.":
    "We'll use this to improve our responses.",
  "Bad answer": "Bad answer",
  "Good answer": "Good answer",
  "Thanks for the feedback!": "Thanks for the feedback!",
  "We're glad this response was helpful.":
    "We're glad this response was helpful.",
  "Message copied to clipboard": "Message copied to clipboard",
  "Searching documents": "Searching documents",
  "Searching sources...": "Searching sources...",
  "Source search complete": "Source search complete",
  Context: "Context",
  "LegalSource.DU": "Dziennik Ustaw (DU)",
  "LegalSource.DU.description":
    "Search across Polish acts in force as of a date",
  "LegalSource.SN": "Sąd Najwyższy (SN)",
  "LegalSource.SN.description": "Search Supreme Court rulings",
  "LegalSource.SP": "Sądy powszechne (SP)",
  "LegalSource.SP.description": "Search common courts rulings",
  "LegalSource.IP": "Interpretacje podatkowe (IP)",
  "LegalSource.IP.description": "Search tax interpretations",
  "LegalSource.TK": "Trybunał Konstytucyjny (TK)",
  "LegalSource.TK.description": "Search Constitutional Tribunal rulings",
  "LegalSource.KIO": "KIO / zamówienia publiczne",
  "LegalSource.KIO.description": "Search public procurement rulings",
  "Relevant content found": "Relevant content found",
  "No relevant documents found": "No relevant documents found",
  "Generating plan": "Generating plan",
  "Plan generated": "Plan generated",
  "Analyzing...": "Analyzing...",
  "Stop generation": "Stop generation",
  Source: "Source",
  Page: "Page",
  "Content not found": "Content not found",
  "Copy message": "Copy message",
  "Are you sure you want to delete this document?":
    "Are you sure you want to delete this document?",
  "Latest topic": "Latest topic",
  "No topics yet": "No topics yet",
  "Calculating...": "Calculating...",
  "Reading document...": "Reading document...",
  "Define your prompt...": "Define your prompt...",
  "Prompt name": "Prompt name",
  "Save as .docx": "Save as .docx",
  "AnyLawyer - Answer.docx": "AnyLawyer - Answer.docx",
  "Message is too long. Max {{max}} characters.":
    "Message is too long. Max {{max}} characters.",
  "Message is too long ({{count}}/{{max}}). Remove {{over}} characters.":
    "Message is too long ({{count}}/{{max}}). Remove {{over}} characters.",

  // Empty states
  "Create your first topic to start the conversation":
    "Create your first topic to start the conversation",

  // Analysis and reflection states
  "Analysis complete": "Analysis complete",
  "Reflecting on the information...": "Reflecting on the information...",

  "Generating citations": "Generating citations",

  // Context-aware loading states
  "Processing your question": "Processing your question",
  "Reading the results": "Reading the results",
  "Analyzing the results": "Analyzing the results",
  "Thinking about the question": "Thinking about the question",
  "Gathering information": "Gathering information",
  "Preparing response": "Preparing response",
  "Reviewing findings": "Reviewing findings",
  "Connecting the dots": "Connecting the dots",
  "Formulating answer": "Formulating answer",
  "Cross-referencing sources": "Cross-referencing sources",
  "Verifying information": "Verifying information",
  "Building comprehensive response": "Building comprehensive response",

  "Open in Braintrust": "Open in Braintrust",
  "Comparison complete": "Comparison complete",
  "Comparing documents...": "Comparing documents...",
  "Excel preview unavailable": "Excel preview unavailable",
  "Previewing Excel files is not possible yet":
    "Previewing Excel files is not possible yet",
  "Click to preview": "Click to preview",
  Preview: "Preview",

  // Error pages
  "Page not found": "Page not found",
  "The URL you entered does not exist": "The URL you entered does not exist",
  "Go home": "Go home",

  "Save as DOCX": "Save as DOCX",

  // New dashboard translations
  "What can I help you with today?": "What can I help you with today?",
  "Ask questions about your legal documents or start a new conversation":
    "Ask questions about your legal documents or start a new conversation",
  "Select a project (optional)": "Select a project (optional)",
  "General conversation": "General conversation",
  docs: "docs",
  "Ask anything about your legal documents...":
    "Ask anything about your legal documents...",
  "Start Conversation": "Start Conversation",
  "Recent Projects": "Recent Projects",
  "View All": "View All",
  "No conversations yet": "No conversations yet",
  documents: "documents",
  "Select project": "Select project",
  // Document tabs and search
  "Search documents...": "Search documents...",
  Project: "Project Documents",
  Organization: "Organization Documents",
  "Legal documents": "Legal Documents",
  "Project Documents": "Project Documents",
  "Organization Documents": "Organization Documents",
  "Available in all organization projects":
    "Available in all organization projects",
  "Available in all project topics": "Available in all project topics",
  "Add from Library": "Add from Library",
  "Legal sources": "Legal sources",
  "Curated legal documents": "Curated legal documents",
  "Attach to Project": "Attach to Project",
  "No documents found": "No documents found",
  "Added {{time}}": "Added {{time}}",
  "No organization documents": "No organization documents",
  "Add documents from your organization's library":
    "Add documents from your organization's library",
  "Attached {{time}}": "Attached {{time}}",
  "No universal documents attached": "No universal documents attached",
  "Attach curated legal documents to your project":
    "Attach curated legal documents to your project",
  "Other organization members will not be able to see this project":
    "Other organization members will not be able to see this project",
  "No universal documents available": "No universal documents available",
  page: "page",
  pages: "pages",
  "No organization documents available": "No organization documents available",

  // Universal document picker
  "Choose documents from your library. Hover over items to preview content.":
    "Choose documents from your library. Hover over items to preview content.",
  "Choose documents from your organization. Hover over items to preview content.":
    "Choose documents from your organization. Hover over items to preview content.",
  document: "document",
  "No documents available": "No documents available",
  "Select or hover over a document to preview":
    "Select or hover over a document to preview",
  Cancel: "Cancel",
  Done: "Done",
  selected: "selected",

  // Project creation
  "Projects allow you to attach & organize your own documents and collaborate with your team.":
    "Projects allow you to attach & organize your own documents and collaborate with your team.",

  // Document selection error messages
  "Failed to update universal document selection. Please try again.":
    "Failed to update universal document selection. Please try again.",
  "Failed to update organization document selection. Please try again.":
    "Failed to update organization document selection. Please try again.",

  // Admin Dashboard
  "Admin Dashboard": "Admin Dashboard",
  "Admin Tools": "Admin Tools",
  Integrations: "Integrations",
  "Organization Stats": "Organization Stats",
  "Organization metrics and insights": "Organization metrics and insights",
  "Select time period": "Select time period",
  "Last 24 hours": "Last 24 hours",
  "Last 7 days": "Last 7 days",
  "Last 30 days": "Last 30 days",
  "Current month": "Current month",
  "Previous month": "Previous month",
  "Pick a date range": "Pick a date range",
  "Quick selection": "Quick selection",
  Update: "Update",
  "Users Active": "Users Active",
  "Documents Uploaded": "Documents Uploaded",
  "Topics Created": "Topics Created",
  "Messages Sent": "Messages Sent",
  "Users who created projects": "Users who created projects",
  "New documents added": "New documents added",
  "New conversation topics": "New conversation topics",
  "Total messages in conversations": "Total messages in conversations",
  "User messages in conversations": "User messages in conversations",
  "Organization Overview": "Organization Overview",
  "Key metrics for your organization over the selected time period":
    "Key metrics for your organization over the selected time period",
  "Time Period": "Time Period",
  "Organization ID": "Organization ID",
  "Data Last Updated": "Data Last Updated",
  Dashboard: "Dashboard",
  "Daily Activity": "Daily Activity",
  "Topics created and messages sent per day":
    "Topics created and messages sent per day",
  "Topics created and user messages sent per day":
    "Topics created and user messages sent per day",
  "Most Active Users": "Most Active Users",
  "Users with the most activity (topics + messages)":
    "Users with the most activity (topics + messages)",
  "Users with the most messages sent": "Users with the most messages sent",
  User: "User",
  Activity: "Activity",
  "Show More": "Show More",
  "Show Less": "Show Less",
  more: "more",

  // Model settings
  "Model settings": "Model settings",
  Model: "Model",
  Reasoning: "Reasoning",
  Verbosity: "Verbosity",

  // Reasoning levels
  "reasoning.None": "Fast",
  "reasoning.Medium": "Standard",
  "reasoning.High": "Deep",

  // Verbosity levels
  "verbosity.Low": "Concise",
  "verbosity.Medium": "Normal",
  "verbosity.High": "Detailed",

  // Reasoning effort descriptions
  "reasoning.none": "Instant responses",
  "reasoning.medium": "Longer processing, balanced reasoning",
  "reasoning.high": "Research-grade intelligence",

  // Verbosity descriptions
  "verbosity.low": "Concise responses",
  "verbosity.medium": "Moderate explanations",
  "verbosity.high": "Detailed and thorough discussions",

  // Model selection
  "model.gpt-5": "GPT-5",
  "model.gpt-5.description": "Latest model",

  // Experimental model notice
  "Experimental Model": "Experimental Model",
  "You're using our new experimental AI model. Your feedback helps us improve!":
    "You're using our new experimental AI model. Your feedback helps us improve!",

  // Web browsing
  "Web browsing": "Web browsing",
  "Enable web browsing to search the internet":
    "Enable web browsing to search the internet",
  "Searching the web...": "Searching the web...",
  "Web search complete": "Web search complete",
  "Reading web results": "Reading web results",

  // Google Drive
  "Google Drive": "Google Drive",
  "Access your Google Drive folders and files":
    "Access your Google Drive folders and files",
  Connect: "Connect",
  Connected: "Connected",
  Disconnect: "Disconnect",
  "Are you sure you want to disconnect Google Drive?":
    "Are you sure you want to disconnect Google Drive?",
  "AI can search your Google Drive files":
    "AI can search your Google Drive files",
  "Searching Google Drive...": "Searching Google Drive...",
  "Google Drive search complete": "Google Drive search complete",
  "Opening file from Google Drive...": "Opening file from Google Drive...",
  "File opened successfully": "File opened successfully",
  "Failed to open file": "Failed to open file",
  "Open in Google Docs": "Open in Google Docs",
  "Open in Google Sheets": "Open in Google Sheets",
  Found: "Found",
  files: "files",
  "No files found in Google Drive": "No files found in Google Drive",
  "Google Drive (entire)": "Google Drive (entire)",
  "Include entire Google Drive": "Include entire Google Drive",
  "Search across all accessible files (My Drive + Shared Drives).":
    "Search across all accessible files (My Drive + Shared Drives).",
  "Whole drive enabled": "Whole drive enabled",
  "Disable whole-drive search to attach folders or files.":
    "Disable whole-drive search to attach folders or files.",

  // OneDrive
  OneDrive: "OneDrive",
  "Are you sure you want to disconnect OneDrive?":
    "Are you sure you want to disconnect OneDrive?",
  "AI can search your OneDrive files": "AI can search your OneDrive files",
  "Connect OneDrive to attach folders": "Connect OneDrive to attach folders",
  "Your OneDrive connection has expired.":
    "Your OneDrive connection has expired.",
  "Searching OneDrive...": "Searching OneDrive...",
  "OneDrive search complete": "OneDrive search complete",
  "No files found in OneDrive": "No files found in OneDrive",
  "Opening file from OneDrive...": "Opening file from OneDrive...",

  // Prompt Improvement
  "Improve prompt": "Improve prompt",
  "Improving prompt...": "Rewriting...",
  "Rewrite your question to be clearer and more effective":
    "Rewrite your question to be clearer and more effective",

  // Read document pages tool
  "Reading document pages...": "Reading document pages...",
  "Document pages loaded": "Document pages loaded",
  Pages: "Pages",

  // Dropbox
  Dropbox: "Dropbox",
  "Access your Dropbox folders and files":
    "Access your Dropbox folders and files",

  // Connected Sources
  "Connected Sources": "Connected Sources",

  // Cloud Drives
  "Cloud Drives": "Cloud Drives",
  "Connect Google Drive to attach folders":
    "Connect Google Drive to attach folders",
  "More providers coming soon": "More providers coming soon",
  "Failed to update folder attachment. Please try again.":
    "Failed to update folder attachment. Please try again.",
  "Limit reached": "Limit reached",
  "You can attach up to 10 folders or files. Please detach some items first.":
    "You can attach up to 10 folders or files. Please detach some items first.",
  "Coming soon": "Coming soon",
  "Dropbox integration coming soon": "Dropbox integration coming soon",

  // Google OAuth Settings
  "Google OAuth Configuration": "Google OAuth Configuration",
  "Configure organization-specific Google OAuth credentials for Google Drive integration.":
    "Configure organization-specific Google OAuth credentials for Google Drive integration.",
  "✓ Using organization-specific credentials":
    "✓ Using organization-specific credentials",
  "Google OAuth Client ID": "Google OAuth Client ID",
  "Enter your Google OAuth Client ID": "Enter your Google OAuth Client ID",
  "Currently using organization-specific Client ID":
    "Currently using organization-specific Client ID",
  "Currently using environment variable fallback":
    "Currently using environment variable fallback",
  "Google OAuth Client Secret": "Google OAuth Client Secret",
  "Enter your Google OAuth Client Secret":
    "Enter your Google OAuth Client Secret",
  "Leave empty to keep current secret unchanged":
    "Leave empty to keep current secret unchanged",
  "Save OAuth Settings": "Save OAuth Settings",
  "Clear Custom Settings": "Clear Custom Settings",
  "Setup Instructions": "Setup Instructions",
  "Organization-specific OAuth credentials allow different organizations to use their own Google Cloud projects for Drive integration.":
    "Organization-specific OAuth credentials allow different organizations to use their own Google Cloud projects for Drive integration.",
  "and create a new project": "and create a new project",
  "and create OAuth 2.0 Client ID (Web application type)":
    "and create OAuth 2.0 Client ID (Web application type)",
  "Add authorized redirect URI:": "Add authorized redirect URI:",
  "Not configured in environment": "Not configured in environment",
  "Copy the Client ID and Client Secret to the form above":
    "Copy the Client ID and Client Secret to the form above",
  "Connect Google Drive using the organization's credentials":
    "Connect Google Drive using the organization's credentials",
  "Important:": "Important:",
  "If Google Drive is already connected with default credentials, you must disconnect and reconnect after setting custom OAuth credentials.":
    "If Google Drive is already connected with default credentials, you must disconnect and reconnect after setting custom OAuth credentials.",

  // Microsoft OAuth Settings (OneDrive)
  "OneDrive Settings": "OneDrive Settings",
  "Microsoft OAuth Configuration": "Microsoft OAuth Configuration",
  "Configure organization-specific Microsoft OAuth credentials for OneDrive integration.":
    "Configure organization-specific Microsoft OAuth credentials for OneDrive integration.",
  "OneDrive OAuth configured": "OneDrive OAuth configured",
  "Configuration required": "Configuration required",
  "Microsoft OAuth Client ID": "Microsoft OAuth Client ID",
  "Enter your Microsoft OAuth Client ID":
    "Enter your Microsoft OAuth Client ID",
  "Microsoft OAuth Client Secret": "Microsoft OAuth Client Secret",
  "Enter your Microsoft OAuth Client Secret":
    "Enter your Microsoft OAuth Client Secret",
  "Use this exact URI in your Microsoft app registration":
    "Use this exact URI in your Microsoft app registration",
  "Saved URI differs from current environment:":
    "Saved URI differs from current environment:",
  "Both Client ID and Client Secret are required":
    "Both Client ID and Client Secret are required",
  "Failed to save OAuth credentials": "Failed to save OAuth credentials",
  "Microsoft OAuth credentials saved successfully":
    "Microsoft OAuth credentials saved successfully",
  "Failed to save credentials": "Failed to save credentials",
  "Redirect URI:": "Redirect URI:",
  Success: "Success",

  // Organization settings
  Organizations: "Organizations",
  "Configure AI model settings for each organization":
    "Configure AI model settings for each organization",
  "No organizations found": "No organizations found",
  "Model updated": "Model updated",
  "Organization model has been updated successfully.":
    "Organization model has been updated successfully.",
  "Failed to update organization model.":
    "Failed to update organization model.",
  "Gemini 3 Pro": "Gemini 3 Pro",
  Custom: "Custom",
  Default: "Default",
  "Saving...": "Saving...",
  Status: "Status",
  member: "member",
  members: "members",
  pending: "pending",
  "No members": "No members",
  Unknown: "Unknown",
  "Pending invitations": "Pending invitations",
  Invite: "Invite",
  "Invite user": "Invite user",
  "Send an invitation to join": "Send an invitation to join",
  "Email address": "Email address",
  Role: "Role",
  Member: "Member",
  Members: "Members",
  Users: "Users",
  "Drive credentials": "Drive credentials",
  "Google Drive credentials": "Google Drive credentials",
  "OneDrive credentials": "OneDrive credentials",
  "Google Drive credentials are configured for this organization.":
    "Google Drive credentials are configured for this organization.",
  "Google Drive credentials are not configured. Configure them in /app/google-oauth.":
    "Google Drive credentials are not configured. Configure them in /app/google-oauth.",
  "OneDrive credentials are configured for this organization.":
    "OneDrive credentials are configured for this organization.",
  "OneDrive credentials are not configured. Configure them in /app/microsoft-oauth.":
    "OneDrive credentials are not configured. Configure them in /app/microsoft-oauth.",
  "Messages (30d)": "Messages (30d)",
  Admin: "Admin",
  "Send invitation": "Send invitation",
  "Sending...": "Sending...",
  "Invitation sent": "Invitation sent",
  "New organization": "New organization",
  "Organization created": "Organization created",
  "The organization has been created successfully.":
    "The organization has been created successfully.",
  "Access Revoked": "Access Revoked",
  "Your organization's trial access has been revoked. Please contact us to discuss further details and continue using the platform.":
    "Your organization's trial access has been revoked. Please contact us to discuss further details and continue using the platform.",
  "Contact Us": "Contact Us",
  "Access status updated": "Access status updated",
  "Organization trial access status has been updated.":
    "Organization trial access status has been updated.",
  "Create a new organization": "Create a new organization",
  "Organization name": "Organization name",
  "Enter organization name": "Enter organization name",
  "Verified domain": "Verified domain",
  "Enter verified domain (e.g. example.com)":
    "Enter verified domain (e.g. example.com)",
  "Users signing up with this email domain will be invited automatically.":
    "Users signing up with this email domain will be invited automatically.",
  "Creating...": "Creating...",
  "Create organization": "Create organization",
  "messages ({{period}})": "messages ({{period}})",
  "The invitation has been sent successfully.":
    "The invitation has been sent successfully.",
  "An error occurred.": "An error occurred.",
  "Msg/User Limit": "Msg/User Limit",
  "No limit": "No limit",
  "Limit updated": "Limit updated",
  "Messages per user limit has been updated successfully.":
    "Messages per user limit has been updated successfully.",
  // Transcriptions
  Transcriptions: "Transcriptions",
  "Transcribe audio files": "Transcribe audio files",
  "New Transcription": "New Transcription",
  "Upload Audio": "Upload Audio",
  "No transcriptions yet": "No transcriptions yet",
  "Upload your first audio file to get started":
    "Upload your first audio file to get started",
  "Transcription Details": "Transcription Details",
  "Delete Transcription": "Delete Transcription",
  "Are you sure you want to delete this transcription?":
    "Are you sure you want to delete this transcription?",
  "Transcription deleted": "Transcription deleted",
  "The transcription has been deleted successfully":
    "The transcription has been deleted successfully",
  "Failed to delete transcription": "Failed to delete transcription",
  "Transcription uploaded": "Transcription uploaded",
  "Your audio file is being processed": "Your audio file is being processed",
  "Processing transcription...": "Processing transcription...",
  "Transcription processed": "Transcription processed",
  "Transcription failed": "Transcription failed",
  Transcript: "Transcript",
  "Audio file": "Audio file",
  "Supported formats: MP3, WAV":
    "Supported formats: MP3, MP4, M4A, WAV, FLAC, OGG, WEBM",
  "Unsupported audio format":
    "{{filename}} is not a supported audio format. Supported: MP3, MP4, M4A, WAV, FLAC, OGG, WEBM.",
  "Convert to document": "Convert to document",
  "Make this transcript searchable in your projects":
    "Make this transcript searchable in your projects",
  "Converted {{time}}": "Converted {{time}}",
  "Converting...": "Converting...",
  "Already converted": "Already converted",
  "Transcription converted to document": "Transcription converted to document",
  Joined: "Joined",
  "Last active": "Last active",
  Never: "Never",

  // Leaflet
  "Leaflet.metaTitle": "AnyLawyer Leaflet",
  "Leaflet.metaDescription":
    "Printable A4 leaflet describing AnyLawyer and pricing.",
  "Leaflet.logoAlt": "AnyLawyer Logo",
  "Leaflet.toolbarHint": "A4 preview — open print dialog to export to PDF.",
  "Leaflet.print": "Print",
  "Leaflet.badge": "A4 printable leaflet",
  "Leaflet.title": "Legal Intelligence, Perfected.",
  "Leaflet.subtitle":
    "The secure AI workspace for legal document analysis. Analyze thousands of documents in minutes. Integrates with Google Drive, OneDrive, and your private cloud. Zero data training, 100% grounded citations.",
  "Leaflet.contactLabel": "Contact",
  "Leaflet.trialLabel": "Trial",
  "Leaflet.trialValue": "30-day free trial",
  "Leaflet.whatIsTitle": "What is AnyLawyer?",
  "Leaflet.whatIsBody":
    "Upload contracts, court files, or regulations. Ask questions in natural language. Get answers with clickable citations to exact pages and paragraphs.",
  "Leaflet.aiAndLawyerTitle": "AI + Lawyer",
  "Leaflet.aiCardTitle": "AI",
  "Leaflet.aiCardBody":
    "Automates review, answers questions, and drafts reports—always linked to evidence.",
  "Leaflet.lawyerCardTitle": "Lawyer",
  "Leaflet.lawyerCardBody":
    "Keeps control: verifies sources, reviews reasoning, and advises clients with confidence.",
  "Leaflet.groundedTitle": "Grounded citations",
  "Leaflet.groundedBody":
    "Every claim is linked directly to the source text for instant verification. Zero black-box answers.",
  "Leaflet.securityTitle": "Security & data sovereignty",
  "Leaflet.security1": "End-to-end encryption for data in transit and at rest",
  "Leaflet.security2": "Strict project and data isolation",
  "Leaflet.security3":
    "Zero-training policy: We never use your data to train our models",
  "Leaflet.security4": "Dedicated private cloud deployment options available",
  "Leaflet.capabilitiesTitle": "Key capabilities",
  "Leaflet.cap1Title": "Automated Due Diligence",
  "Leaflet.cap1Body":
    "Identify and extract specific provisions from hundreds of documents instantly with citation-backed accuracy.",
  "Leaflet.cap2Title": "Instant Case Research",
  "Leaflet.cap2Body":
    "Ask complex questions across your entire case file and get answers rooted in evidence.",
  "Leaflet.cap3Title": "Report Generation",
  "Leaflet.cap3Body":
    "Generate accurate and comprehensive legal memos and client reports directly from your case documents.",
  "Leaflet.cap4Title": "Deposition & Testimony Review",
  "Leaflet.cap4Body":
    "Process and analyze transcripts to identify inconsistencies, key admissions, and argumentative patterns.",
  "Leaflet.cap5Title": "Grounded Citations",
  "Leaflet.cap5Body":
    "Every claim is linked directly to the source text for instant verification. Zero black-box answers.",
  "Leaflet.cap6Title": "Audit Trail",
  "Leaflet.cap6Body":
    "Follow the AI's reasoning step-by-step before advising your client.",
  "Leaflet.cap7Title": "Legal Sources Integration",
  "Leaflet.cap7Body":
    "Search court rulings, tax interpretations, and legislation from official Polish legal databases.",
  "Leaflet.pricingTitle": "Pricing",
  "Leaflet.pricingSubtitle":
    "Simple, transparent pricing. No hidden fees, no long-term commitments.",
  "Leaflet.proTitle": "Professional",
  "Leaflet.proPrice": "49€",
  "Leaflet.proPriceSuffix": "per month, per user",
  "Leaflet.pro1": "Unlimited queries",
  "Leaflet.pro2": "Unlimited project workspaces",
  "Leaflet.pro3": "SaaS cloud access (EU-hosted)",
  "Leaflet.pro4": "Citation-backed answers",
  "Leaflet.pro5": "State-of-art fine-tuned models",
  "Leaflet.entTitle": "Enterprise",
  "Leaflet.entPrice": "Custom",
  "Leaflet.entPriceSuffix": "contact for pricing",
  "Leaflet.ent1": "On-prem / Bring Your Own Cloud",
  "Leaflet.ent2": "SSO (Okta, AD) & Advanced IAM",
  "Leaflet.ent3": "Priority 24/7 support",
  "Leaflet.ent4": "Custom SLA & Contracts",
  "Leaflet.footerTitle": "Book a demo",
  "Leaflet.footerBody":
    "Email us to see AnyLawyer on your documents and workflows.",
  "Leaflet.tablePlan": "Plan",
  "Leaflet.tableFree": "Free (14 days)",
  "Leaflet.tablePlus": "Plus",
  "Leaflet.tablePro": "Pro",
  "Leaflet.tablePrice": "Price per seat/month",
  "Leaflet.tablePlusPrice": "199zł/seat",
  "Leaflet.tableProPrice": "499zł/seat",
  "Leaflet.tableDocuments": "Documents",
  "Leaflet.tableUnlimited": "Unlimited",
  "Leaflet.tableMessages": "Messages per month",
  "Leaflet.tableExtras": "Extras",
  "Leaflet.tableProExtras": "Transcriptions, Google Drive/OneDrive",

  // Pricing page
  "Pricing.metaTitle": "Pricing - AnyLawyer",
  "Pricing.metaDescription": "Compare AnyLawyer plans and pricing.",
  "Pricing.logoAlt": "AnyLawyer Logo",
  "Pricing.pageTitle": "Pricing",
  "Pricing.saveAsPDF": "Save as PDF",
  "Pricing.headline": "Choose the right plan for your practice",
  "Pricing.subheadline":
    "All plans include a 30-day free trial. No credit card required.",
  "Pricing.features": "Features",
  "Pricing.planFree": "Free",
  "Pricing.planPlus": "Plus",
  "Pricing.planPro": "Pro",
  "Pricing.planEnterprise": "Enterprise",
  "Pricing.trialPeriod": "30-day trial",
  "Pricing.perSeatMonth": "per seat/month",
  "Pricing.free": "Free",
  "Pricing.custom": "Custom",
  "Pricing.monthly": "Monthly",
  "Pricing.annual": "Annual",
  "Pricing.poc": "POC available",
  "Pricing.unlimited": "Unlimited",
  "Pricing.basic": "Basic",
  "Pricing.advanced": "Advanced",
  "Pricing.allModels": "All models",
  "Pricing.allModelsBYOK": "All + BYOK",
  "Pricing.fast": "Fast",
  "Pricing.fastStandard": "Fast + Standard",
  "Pricing.allModes": "All modes",
  "Pricing.euOrCustom": "EU or custom",
  "Pricing.community": "Community",
  "Pricing.email": "Email",
  "Pricing.priorityEmail": "Priority email",
  "Pricing.dedicated247": "Dedicated 24/7",
  "Pricing.contactUs": "Contact us",
  "Pricing.categoryBasics": "Basics",
  "Pricing.categoryLimits": "Usage Limits",
  "Pricing.categoryAI": "AI & Models",
  "Pricing.categoryFeatures": "Features",
  "Pricing.categoryIntegrations": "Integrations",
  "Pricing.categorySecurity": "Security & Compliance",
  "Pricing.categorySupport": "Support",
  "Pricing.featurePrice": "Price per seat",
  "Pricing.featureBilling": "Billing",
  "Pricing.featureTrialDays": "Trial period (days)",
  "Pricing.featureSeats": "Seats",
  "Pricing.featureMessagesMonth": "Messages per month",
  "Pricing.featureDocuments": "Documents",
  "Pricing.featureProjects": "Projects",
  "Pricing.featureStoragePerSeat": "Storage per seat",
  "Pricing.featureMaxFileSize": "Max file size",
  "Pricing.featureAIModels": "AI models",
  "Pricing.featureReasoningModes": "Reasoning modes",
  "Pricing.featureCitations": "Grounded citations",
  "Pricing.featureAuditTrail": "AI reasoning audit trail",
  "Pricing.featureDocumentGeneration": "Document generation",
  "Pricing.featureTranscriptions": "Audio transcriptions",
  "Pricing.featureDocxExport": "Export to DOCX",
  "Pricing.featureGoogleDrive": "Google Drive integration",
  "Pricing.featureOneDrive": "OneDrive integration",
  "Pricing.featureLegalSources": "Polish legal sources",
  "Pricing.featureWebSearch": "Web search",
  "Pricing.featureAPI": "API access",
  "Pricing.featureEncryption": "End-to-end encryption",
  "Pricing.featureDataResidency": "Data residency",
  "Pricing.featureZeroTraining": "Zero data training",
  "Pricing.featureSSO": "SSO (Okta, Azure AD)",
  "Pricing.featureAdvancedIAM": "Advanced IAM",
  "Pricing.featureAuditLogs": "Audit logs",
  "Pricing.featurePrivateCloud": "Private cloud deployment",
  "Pricing.featureBYOC": "Bring Your Own Cloud",
  "Pricing.featureSupportLevel": "Support",
  "Pricing.featureOnboarding": "Onboarding session",
  "Pricing.featureAccountManager": "Dedicated account manager",
  "Pricing.featureCustomSLA": "Custom SLA",
  "Pricing.featureCustomContract": "Custom contract",
  "Pricing.ctaText": "Ready to get started?",
  "Pricing.ctaStartTrial": "Start free trial",
  "Pricing.ctaContactSales": "Contact sales",
  "Pricing.legalSourcesFootnote":
    "include: Polish legislation (acts, regulations), tax interpretations, common court and Supreme Court rulings, Constitutional Tribunal rulings, and KIO rulings (public procurement).",
  "Pricing.footerNote": "Prices shown in PLN. VAT not included.",

  // Landing page - Navigation
  "Landing.nav.features": "Features",
  "Landing.nav.security": "Security",
  "Landing.nav.pricing": "Pricing",
  "Landing.nav.testimonials": "Testimonials",
  "Landing.nav.faq": "FAQ",
  "Landing.nav.login": "Log In",
  "Landing.nav.startTrial": "Start Free Trial",
  "Landing.nav.goToApp": "Go to Anylawyer",

  // Landing page - Hero section
  "Landing.hero.badge": "New: Adjustable Summary Depth & Reasoning Power",
  "Landing.hero.title1": "Legal Intelligence,",
  "Landing.hero.title2": "Perfected.",
  "Landing.hero.description":
    "The secure AI workspace for high-stakes document analysis. Analyze thousands of documents in minutes. Integrated securely with your private cloud, ensuring zero data training and 100% grounded citations.",
  "Landing.hero.bookDemo": "Book Demo",
  "Landing.hero.feature1": "Fine-tuned Legal Models",
  "Landing.hero.feature2": "Zero Data Training",
  "Landing.hero.feature3": "14-Day Free Trial",

  // Landing page - Trusted by section
  "Landing.trustedBy": "Trusted by Leading Law Firms",

  // Landing page - Bento grid features
  "Landing.bento.title": "Transform Your Legal Practice",
  "Landing.bento.description":
    "AnyLawyer helps legal professionals save time, reduce errors, and gain deeper insights from their documents.",
  "Landing.bento.dueDiligence.title": "Automated Due Diligence",
  "Landing.bento.dueDiligence.description":
    "Identify and extract specific provisions from hundreds of documents instantly. Automate the review of contracts, leases, and discovery materials with citation-backed accuracy.",
  "Landing.bento.caseResearch.title": "Instant Case Research",
  "Landing.bento.caseResearch.description":
    "Stop manual searching. Ask complex questions across your entire case file and get answers rooted in evidence.",
  "Landing.bento.reportGen.title": "Report Generation",
  "Landing.bento.reportGen.description":
    "Generate accurate and comprehensive legal memos and client reports directly from your case documents.",
  "Landing.bento.deposition.title": "Deposition & Testimony Review",
  "Landing.bento.deposition.description":
    "Process and analyze court transcripts to identify inconsistencies, key admissions, and argumentative patterns.",
  "Landing.bento.citations.title": "Grounded Citations",
  "Landing.bento.citations.description":
    "Every claim is linked directly to the source text for instant verification. Zero black-box answers.",
  "Landing.bento.auditTrail.title": "Audit Trail",
  "Landing.bento.auditTrail.description":
    "Follow the AI's reasoning step-by-step. Understand exactly how a conclusion was reached before advising your client.",
  "Landing.bento.questions.q1": "What are the key provisions in section 3.2?",
  "Landing.bento.questions.q2": "When does this contract expire?",
  "Landing.bento.questions.q3": "What are my obligations under this agreement?",
  "Landing.bento.questions.q4":
    "Is there a non-compete clause in this document?",
  "Landing.bento.questions.q5": "What are the termination conditions?",
  "Landing.bento.questions.q6": "Who are the involved parties?",
  "Landing.bento.questions.q7": "What are the payment terms?",
  "Landing.bento.questions.q8": "Are there any indemnity clauses?",
  "Landing.bento.questions.q9": "What is the governing law?",
  "Landing.bento.questions.q10": "How can this agreement be amended?",

  // Landing page - Complete Legal Toolkit section
  "Landing.toolkit.title": "Complete Legal Toolkit",
  "Landing.toolkit.description":
    "Everything you need to manage complex legal workflows in one secure platform.",
  "Landing.toolkit.dataHeavy.title": "Built for Data-Heavy Litigation",
  "Landing.toolkit.dataHeavy.description":
    "AnyLawyer's Deep Context Analysis supports hundreds of thousands of documents per project, enabling full-corpus discovery.",
  "Landing.toolkit.citations.title": "Citations & Verification",
  "Landing.toolkit.citations.description":
    "All answers are supported by pinpoint citations from the original document. Trust but verify.",
  "Landing.toolkit.privateTenant.title": "Private Tenant Deployment",
  "Landing.toolkit.privateTenant.description":
    "Optional deployment to your own private cloud infrastructure. We support AWS, GCP and Azure.",
  "Landing.toolkit.multiStep.title": "Multi-Step Legal Reasoning",
  "Landing.toolkit.multiStep.description":
    "Our AI breaks down complex legal queries into logical steps to find answers buried across hundreds of files.",
  "Landing.toolkit.integration.title": "Ecosystem Integration",
  "Landing.toolkit.integration.description":
    "AnyLawyer connects to your DMS. Google Drive integration available; SharePoint and OneDrive coming soon.",
  "Landing.toolkit.dataIsolation.title": "Strict Data Isolation",
  "Landing.toolkit.dataIsolation.description":
    "Project-level isolation ensures client data never crosses. Encrypted at rest and in transit. EU-hosted.",
  "Landing.toolkit.export.title": "Export to DOCX",
  "Landing.toolkit.export.description":
    "Export your answers to DOCX format for easy sharing and collaboration.",
  "Landing.toolkit.team.title": "Team Collaboration",
  "Landing.toolkit.team.description":
    "Share documents, insights, prompts, and search results with your team. Create collaborative workspaces for case research and knowledge sharing.",
  "Landing.toolkit.comingSoon": "Coming Soon",

  // Landing page - Security section
  "Landing.security.title": "Enterprise-Ready Compliance & Data Sovereignty",
  "Landing.security.description":
    "We understand that client confidentiality is paramount. That's why AnyLawyer is built with a security-first architecture designed for modern legal practice.",
  "Landing.security.encryption":
    "End-to-end encryption for data in transit and at rest",
  "Landing.security.isolation": "Strict project and data isolation",
  "Landing.security.zeroTraining":
    "Zero-training policy: We never use your data to train our models",
  "Landing.security.privateCloud":
    "Dedicated private cloud deployment options available",
  "Landing.security.encryptionLabel": "Encryption",
  "Landing.security.dataResidency": "Data Residency",
  "Landing.security.dataResidencyValue": "EU (GDPR Compliant)",
  "Landing.security.auditTrailLabel": "Audit Trail",
  "Landing.security.auditTrailValue": "Immutable Logs",

  // Landing page - Pricing section
  "Landing.pricing.title": "Simple, Transparent Pricing",
  "Landing.pricing.description":
    "Choose the plan that best fits your practice's needs. No hidden fees, no long-term commitments.",
  "Landing.pricing.trialNote": "All plans include a 30-day free trial",
  "Landing.pricing.perSeatMonth": "per seat/month",
  "Landing.pricing.mostPopular": "Most Popular",

  // Free plan
  "Landing.pricing.free": "Free",
  "Landing.pricing.freePrice": "0 zł",
  "Landing.pricing.freeDesc": "Try AnyLawyer risk-free for 30 days",
  "Landing.pricing.freeTrial": "30-day trial",

  // Plus plan
  "Landing.pricing.plus": "Plus",
  "Landing.pricing.plusPrice": "199 zł",
  "Landing.pricing.plusDesc": "For individual lawyers and small practices",

  // Pro plan
  "Landing.pricing.pro": "Pro",
  "Landing.pricing.proPrice": "499 zł",
  "Landing.pricing.proDesc": "For growing teams with advanced needs",

  // Enterprise plan
  "Landing.pricing.enterprise": "Enterprise",
  "Landing.pricing.enterprisePrice": "Custom",
  "Landing.pricing.enterpriseDesc": "Complete solution for large organizations",
  "Landing.pricing.contactForPricing": "contact for pricing",
  "Landing.pricing.contactSales": "Contact sales",

  "Landing.pricing.getStarted": "Get started",
  "Landing.pricing.startTrial": "Start free trial",

  // Features
  "Landing.pricing.feature.messages50": "50 messages/month",
  "Landing.pricing.feature.messages500": "500 messages/month",
  "Landing.pricing.feature.messagesUnlimited": "Unlimited messages",
  "Landing.pricing.feature.projects3": "3 projects",
  "Landing.pricing.feature.projectsUnlimited": "Unlimited projects",
  "Landing.pricing.feature.documentsUnlimited": "Unlimited documents",
  "Landing.pricing.feature.basicModels": "Basic AI models",
  "Landing.pricing.feature.allModels": "All AI models",
  "Landing.pricing.feature.fastReasoning": "Fast reasoning mode",
  "Landing.pricing.feature.allReasoningModes": "All reasoning modes",
  "Landing.pricing.feature.citations": "Grounded citations",
  "Landing.pricing.feature.docxExport": "Export to DOCX",
  "Landing.pricing.feature.transcriptions": "Audio transcriptions",
  "Landing.pricing.feature.googleDrive": "Google Drive integration",
  "Landing.pricing.feature.legalSources": "Polish legal sources",
  "Landing.pricing.feature.webSearch": "Web search",
  "Landing.pricing.feature.prioritySupport": "Priority support",
  "Landing.pricing.feature.onPrem": "On-prem / BYOC deployment",
  "Landing.pricing.feature.sso": "SSO & Advanced IAM",
  "Landing.pricing.feature.customSla": "Custom SLA & Contracts",
  "Landing.pricing.feature.dedicatedManager": "Dedicated account manager",
  "Landing.pricing.feature.auditLogs": "Audit logs & Analytics",
  "Landing.pricing.feature.communitySupport": "Community support",
  "Landing.pricing.feature.emailSupport": "Email support",
  "Landing.pricing.feature.priorityEmailSupport": "Priority email support",
  "Landing.pricing.feature.dedicated247": "Dedicated 24/7 support",

  // Landing page - FAQ section
  "Landing.faq.title": "Frequently",
  "Landing.faq.title2": "Asked",
  "Landing.faq.title3": "Questions",
  "Landing.faq.subtitle":
    "Everything you need to know about the product and billing.",
  "Landing.faq.q1": "How do you ensure the security of our client data?",
  "Landing.faq.a1":
    "We implement industry-standard encryption protocols and maintain strict data isolation between tenants. Your documents and information are never used to train our global models, ensuring complete confidentiality and data protection.",
  "Landing.faq.q2": "What integrations are available for document management?",
  "Landing.faq.a2":
    "We offer native Google Drive integration for seamless document synchronization. Additionally, Dropbox and Microsoft SharePoint integrations are available upon request to accommodate your firm's existing workflow.",
  "Landing.faq.q3": "How reliable are the AI-generated summaries and analyses?",
  "Landing.faq.a3":
    "Our models are specifically fine-tuned for legal document analysis. Every summary includes precise citations that link directly to the source material, enabling you to verify and validate all generated content with confidence.",
  "Landing.faq.q4":
    "Do you provide enterprise pricing for larger organizations?",
  "Landing.faq.a4":
    "Yes, we offer customized pricing packages for firms with 10 or more users. Please contact our sales team to discuss volume discounts and enterprise-specific features tailored to your organization's needs.",
  "Landing.faq.q5": "Where is our data stored and processed?",
  "Landing.faq.a5":
    "All data is stored and processed exclusively within EU data centers to ensure compliance with data sovereignty requirements. We can provide specific regional hosting options to meet your jurisdiction's data residency regulations.",
  "Landing.faq.q6": "What service level agreements do you offer?",
  "Landing.faq.a6":
    "Enterprise plans include a 99.9% uptime SLA with 24/7 priority support and dedicated account management. We provide guaranteed response times, proactive monitoring, and regular status updates to ensure business continuity.",
  "Landing.faq.q7": "What are your data retention and deletion policies?",
  "Landing.faq.a7":
    "You maintain full control over your data retention policies. We comply with your specified retention requirements and provide secure, certified data deletion upon request or contract termination, ensuring complete data lifecycle management.",
  "Landing.faq.q8": "Is our data used to train AI models?",
  "Landing.faq.a8":
    "No. Your data is never used to train models by our AI providers (OpenAI/Google) — they cannot use it for model training. Your data is used solely to generate responses within the service.",

  // Landing page - Final CTA section
  "Landing.cta.title": "Ready to modernize your practice?",
  "Landing.cta.description":
    "Join the fastest growing legal AI platform today. No credit card required for trial.",

  // Landing page - Footer
  "Landing.footer.tagline":
    "Empowering legal professionals with next-generation AI tools.",
  "Landing.footer.product": "Product",
  "Landing.footer.legal": "Legal",
  "Landing.footer.privacyPolicy": "Privacy Policy",
  "Landing.footer.termsOfService": "Terms of Service",
  "Landing.footer.contactUs": "Contact Us",
  "Landing.footer.copyright": "© 2025 AnyLawyer. All rights reserved.",

  // Organization Documents page
  "Manage documents that are available across all projects in your organization":
    "Manage documents that are available across all projects in your organization",
  "Add Documents": "Add Documents",
  "Add Organization Documents": "Add Organization Documents",
  "Upload Files": "Upload Files",
  "Choose Files": "Choose Files",
  "You can select multiple files. Max file size: {{size}}MB each.":
    "You can select multiple files. Max file size: {{size}}MB each.",
  "Selected Files ({{count}})": "Selected Files ({{count}})",
  "Document name...": "Document name...",
  "Upload {{count}} Document": "Upload {{count}} Document",
  "Upload {{count}} Documents": "Upload {{count}} Documents",
  "Upload Progress": "Upload Progress",
  "{{completed}} / {{total}} completed": "{{completed}} / {{total}} completed",
  "No organization documents yet. Add your first one above.":
    "No organization documents yet. Add your first one above.",
  Name: "Name",
  Created: "Created",
  Actions: "Actions",
  "uploading {{progress}}%": "uploading {{progress}}%",
  queued: "queued",
  "upload complete": "upload complete",
  "upload failed": "upload failed",
  "Unsupported file type": "Unsupported file type",
  "Duplicate file": "Duplicate file",
  "{{file}} is already selected.": "{{file}} is already selected.",
  "No files selected": "No files selected",
  "Please select at least one file to upload.":
    "Please select at least one file to upload.",
  "Missing names": "Missing names",
  "Please provide names for all selected documents.":
    "Please provide names for all selected documents.",
  "Upload started": "Upload started",
  "Starting upload of {{count}} document(s).":
    "Starting upload of {{count}} document(s).",
  "Document uploaded": "Document uploaded",
  "{{name}} has been uploaded and is being processed.":
    "{{name}} has been uploaded and is being processed.",
  "Upload timeout": "Upload timeout",
  "Document upload is taking longer than expected. Please refresh the page to check if it appeared.":
    "Document upload is taking longer than expected. Please refresh the page to check if it appeared.",
  "Document processed": "Document processed",
  "{{name}} has been successfully processed.":
    "{{name}} has been successfully processed.",
  "Document processing failed": "Document processing failed",
  "{{name}} failed to process.": "{{name}} failed to process.",

  // ELI Legal Acts Browser
  "Legal Acts Database": "Legal Acts Database",
  "Browse and search Polish legal acts from the official ELI database":
    "Browse and search Polish legal acts from the official ELI database",
  "Search acts by title...": "Search acts by title...",
  Search: "Search",
  Clear: "Clear",
  Publishers: "Publishers",
  "No publishers found. Run the Sejm sync to populate the database.":
    "No publishers found. Run the Sejm sync to populate the database.",
  acts: "acts",
  "total acts": "total acts",
  "Legal Acts": "Legal Acts",
  Year: "Year",
  Position: "Position",
  "All years": "All years",
  Showing: "Showing",
  Acts: "Acts",
  "Acts from": "Acts from",
  "All acts from this publisher": "All acts from this publisher",
  "No acts found for the selected criteria.":
    "No acts found for the selected criteria.",
  Previous: "Previous",
  of: "of",
  Next: "Next",
  "Search results for": "Search results for",
  "No acts found matching your search.": "No acts found matching your search.",
  Address: "Address",
  Date: "Date",
  "In force": "In force",
  "Not in force": "Not in force",
  Metadata: "Metadata",
  ELI: "ELI",
  Type: "Type",
  "Announcement Date": "Announcement Date",
  "Entry Into Force": "Entry Into Force",
  "Valid From": "Valid From",
  Promulgation: "Promulgation",
  "Expiration Date": "Expiration Date",
  "Repeal Date": "Repeal Date",
  "Legal Status Date": "Legal Status Date",
  "Change Date": "Change Date",
  Volume: "Volume",
  "Authorized Body": "Authorized Body",
  Obligated: "Obligated",
  "Released By": "Released By",
  Comments: "Comments",
  References: "References",
  references: "references",
  "No references found for this act.": "No references found for this act.",
  "No ELI ID": "No ELI ID",
  Texts: "Texts",
  "Text Files": "Text Files",
  "Text Versions": "Text Versions",
  "Full Act PDF": "Full Act PDF",
  "Consolidated document": "Consolidated document",
  "No text files available for this act.":
    "No text files available for this act.",
  "Consolidated text": "Consolidated text",
  "Published text": "Published text",
  "Uniform text": "Uniform text",
  "Historical text": "Historical text",
  "Initial text": "Initial text",
  Text: "Text",
  Directives: "Directives",
  "EU Directives": "EU Directives",
  "No EU directives linked to this act.":
    "No EU directives linked to this act.",
  Prints: "Prints",
  "Parliamentary Prints": "Parliamentary Prints",
  "No parliamentary prints linked to this act.":
    "No parliamentary prints linked to this act.",
  Print: "Print",
  Term: "Term",
  View: "View",
  "Full Text": "Full Text",
  "Untitled Act": "Untitled Act",
  Title: "Title",
  "Download PDF": "Download PDF",
  "Official PDF": "Official PDF",
  "Open in new tab": "Open in new tab",
  "PDF Preview": "PDF Preview",
  "Sejm API": "Sejm API",
  "No full text available for this act.":
    "No full text available for this act.",
  "Full text not available. See PDF preview.":
    "Full text not available. See PDF preview.",
};

// Polish translations
const pl = {
  // I use it to no loose typing when calling t() function but also to be able to call it on arbitrary validation errors
  __generic: "__generic",

  "Hello!": "Cześć!",
  Save: "Zapisz",
  Delete: "Usuń",
  Back: "Wstecz",
  None: "Brak",
  Dismiss: "Zamknij",

  // Auth
  Logout: "Wyloguj",
  "Log in": "Zaloguj się",

  // Projects
  "Add project": "Nowy projekt",
  "New project": "Nowy projekt",
  "Delete project": "Usuń projekt",
  "Project name": "Nazwa projektu",
  Projects: "Projekty",
  "Search projects...": "Szukaj projektów...",
  "No projects found": "Nie znaleziono projektów",
  Questions: "Pytania",
  Ask: "Zapytaj",
  Question: "Pytanie",
  Answer: "Odpowiedź",
  "Project with that name already exists": "Projekt o tej nazwie już istnieje",
  "Name should have at least 3 letters":
    "Nazwa powinna mieć przynajmniej 3 litery",
  "Name cannot be empty": "Nazwa nie może być pusta",
  "Processing data...": "Przetwarzam dane...",
  source: "źrodło",
  "Are you sure you want to delete this project?":
    "Czy na pewno chcesz usunąć ten projekt?",
  "Rename project": "Zmień nazwę projektu",
  "Private project": "Prywatny projekt",
  "Project description": "Opis projektu",
  "Add a description...": "Dodaj opis...",
  "Start a new conversation...": "Rozpocznij nową rozmowę...",

  // Documents
  "Attach documents": "Załącz dokumenty",
  "Delete document": "Usuń dokument",
  "Document removed": "Dokument usunięty",
  "The document has been removed from the upload queue":
    "Dokument został usunięty z kolejki przesyłania",
  "Document deleted": "Dokument usunięty",
  "The document has been deleted successfully":
    "Dokument został pomyślnie usunięty",
  "Failed to delete document": "Nie udało się usunąć dokumentu",
  "An error occurred while deleting the document":
    "Wystąpił błąd podczas usuwania dokumentu",
  Documents: "Dokumenty",
  "Upload documents": "Prześlij dokumenty",
  "Uploading...": "Przesyłanie...",
  "Processing...": "Przetwarzanie...",
  "Uploading {{file}} failed": "Przesyłanie {{file}} nie powiodło się",
  "Uploaded {{time}}": "Przesłano {{time}}",
  "File too big": "Plik jest za duży",
  "{{file}} is too big. Max file size is {{size}}.":
    "{{file}} jest za duży. Maksymalny rozmiar pliku to {{size}}.",
  "File already exists": "Plik już istnieje",
  "{{file}} already exists": "{{file}} już istnieje",
  "Invalid file type": "Nieprawidłowy typ pliku",
  "{{file}} is not a supported file type.":
    "{{file}} nie jest obsługiwanym typem pliku",
  "Drop files here to upload": "Upuść pliki tutaj, aby je przesłać",
  "File processing failed. Remove the file and try again.":
    "Przetwarzanie pliku nie powiodło się. Usuń plik i spróbuj ponownie.",
  Error: "Błąd",
  "Failed to update document selection. Please try again.":
    "Nie udało się zaktualizować wyboru dokumentu. Spróbuj ponownie.",
  "Document not found": "Dokument nie znaleziony",
  "Rename document": "Zmień nazwę dokumentu",
  "No documents yet": "Brak dokumentów",
  "Upload your first document to get started":
    "Prześlij pierwszy dokument, aby rozpocząć",
  "Document name": "Nazwa dokumentu",
  "{{file}} is not a supported file type":
    "{{file}} nie jest obsługiwanym typem pliku",
  "Document extension mismatch": "Nieprawidłowe rozszerzenie dokumentu",

  // Topics
  Topics: "Tematy",
  Messages: "Wiadomości",
  "Recent topics": "Ostatnie tematy",
  "New topic": "Nowy temat",
  "New topic name": "Nazwa tematu",
  "Created {{time}}": "Utworzono {{time}}",
  "Delete topic": "Usuń temat",
  Untitled: "Bez nazwy",
  "Topic name": "Nazwa tematu",
  "Rename topic": "Zmień nazwę tematu",

  // Chat related
  "Thank you for your feedback!": "Dziękujemy za opinię!",
  "We'll use this to improve our responses.":
    "Wykorzystamy ją do ulepszenia naszych odpowiedzi.",
  "Bad answer": "Zła odpowiedź",
  "Good answer": "Dobra odpowiedź",
  "Thanks for the feedback!": "Dziękujemy za opinię!",
  "We're glad this response was helpful.":
    "Cieszymy się, że ta odpowiedź była pomocna.",
  "Message copied to clipboard": "Skopiowano wiadomość do schowka",
  "Searching documents": "Przeszukuję dokumenty",
  "Searching sources...": "Przeszukuję źródła...",
  "Source search complete": "Przeszukano źródła",
  Context: "Kontekst",
  "LegalSource.DU": "Dziennik Ustaw (DU)",
  "LegalSource.DU.description":
    "Wyszukuj w aktach prawnych obowiązujących na dany dzień",
  "LegalSource.SN": "Sąd Najwyższy (SN)",
  "LegalSource.SN.description": "Wyszukuj orzeczenia Sądu Najwyższego",
  "LegalSource.SP": "Sądy powszechne (SP)",
  "LegalSource.SP.description": "Wyszukuj orzeczenia sądów powszechnych",
  "LegalSource.IP": "Interpretacje podatkowe (IP)",
  "LegalSource.IP.description": "Wyszukuj interpretacje podatkowe",
  "LegalSource.TK": "Trybunał Konstytucyjny (TK)",
  "LegalSource.TK.description": "Wyszukuj orzeczenia TK",
  "LegalSource.KIO": "KIO / zamówienia publiczne",
  "LegalSource.KIO.description": "Wyszukuj orzeczenia KIO",
  "Relevant content found": "Przeszukano dokumenty",
  "No relevant documents found": "Nie znaleziono pasujących dokumentów",
  "Generating plan": "Generuję plan",
  "Plan generated": "Plan wygenerowany",
  "Analyzing...": "Analizuję...",
  "Stop generation": "Zatrzymaj generowanie",
  Source: "Źródło",
  Page: "Strona",
  "Content not found": "Nie znaleziono treści",
  "Copy message": "Kopiuj wiadomość",
  "Are you sure you want to delete this document?":
    "Czy na pewno chcesz usunąć ten dokument?",
  "Latest topic": "Ostatni temat",
  "No topics yet": "Brak tematów",
  "Calculating...": "Obliczanie...",
  "Reading document...": "Czytanie dokumentu...",
  "Define your prompt...": "Dodaj swój prompt...",
  "Prompt name": "Nazwa prompta",
  "Save as .docx": "Zapisz jako .docx",
  "AnyLawyer - Answer.docx": "AnyLawyer - Odpowiedź.docx",
  "Message is too long. Max {{max}} characters.":
    "Wiadomość jest za długa. Maksymalnie {{max}} znaków.",
  "Message is too long ({{count}}/{{max}}). Remove {{over}} characters.":
    "Wiadomość jest za długa ({{count}}/{{max}}). Usuń {{over}} znaków.",

  // Empty states
  "Create your first topic to start the conversation":
    "Utwórz pierwszy temat, aby rozpocząć rozmowę",

  // Analysis and reflection states
  "Analysis complete": "Analiza zakończona",
  "Reflecting on the information...": "Badanie informacji...",

  "Generating citations": "Generuję cytaty",

  // Context-aware loading states
  "Processing your question": "Przetwarzam pytanie",
  "Reading the results": "Czytam wyniki",
  "Analyzing the results": "Analizuję wyniki",
  "Thinking about the question": "Zastanawiam się nad pytaniem",
  "Gathering information": "Zbieranie informacji",
  "Preparing response": "Przygotowuję odpowiedź",
  "Reviewing findings": "Przeglądam wyniki",
  "Connecting the dots": "Łączę informacje",
  "Formulating answer": "Formułuję odpowiedź",
  "Cross-referencing sources": "Sprawdzam źródła",
  "Verifying information": "Weryfikuję informacje",
  "Building comprehensive response": "Tworzę szczegółową odpowiedź",

  "Open in Braintrust": "Otwórz w Braintrust",
  "Comparison complete": "Porównanie zakończone",
  "Comparing documents...": "Porównuję dokumenty...",
  "Excel preview unavailable": "Podgląd pliku Excel niedostępny",
  "Previewing Excel files is not possible yet":
    "Podgląd plików Excel nie jest jeszcze możliwy",
  "Click to preview": "Kliknij aby zobaczyć podgląd",
  Preview: "Podgląd",

  // Error pages
  "Page not found": "Strona nie znaleziona",
  "The URL you entered does not exist":
    "Adres URL, który podałeś, nie istnieje",
  "Go home": "Strona główna",

  "Save as DOCX": "Zapisz jako DOCX",

  // New dashboard translations
  "What can I help you with today?": "W czym mogę Ci dzisiaj pomóc?",
  "Ask questions about your legal documents or start a new conversation":
    "Zadawaj pytania o swoje dokumenty prawne lub rozpocznij nową rozmowę",
  "Select a project (optional)": "Wybierz projekt (opcjonalnie)",
  "General conversation": "Ogólna rozmowa",
  docs: "dok.",
  "Ask anything about your legal documents...":
    "Zapytaj o cokolwiek dotyczące swoich dokumentów prawnych...",
  "Start Conversation": "Rozpocznij rozmowę",
  "Recent Projects": "Ostatnie projekty",
  "View All": "Zobacz wszystkie",
  "No conversations yet": "Brak rozmów",
  documents: "dokumenty",
  "Select project": "Wybierz projekt",
  // Document tabs and search
  "Search documents...": "Szukaj dokumentów...",
  Project: "Dokumenty Projektu",
  Organization: "Dokumenty Organizacji",
  "Legal documents": "Dokumenty prawne",
  "Project Documents": "Dokumenty projektu",
  "Organization Documents": "Dokumenty organizacji",
  "Available in all organization projects":
    "Dostępne we wszystkich projektach organizacji",
  "Available in all project topics": "Dostępne we wszystkich tematach projektu",
  "Add from Library": "Dodaj z biblioteki",
  "Legal sources": "Źródła prawa",
  "Curated legal documents": "Wyselekcjonowane dokumenty prawne",
  "Attach to Project": "Załącz do projektu",
  "No documents found": "Nie znaleziono dokumentów",
  "Added {{time}}": "Dodano {{time}}",
  "No organization documents": "Brak dokumentów organizacji",
  "Add documents from your organization's library":
    "Dodaj dokumenty z biblioteki organizacji",
  "Attached {{time}}": "Załączono {{time}}",
  "No universal documents attached":
    "Brak załączonych dokumentów uniwersalnych",
  "Attach curated legal documents to your project":
    "Załącz wyselekcjonowane dokumenty prawne do projektu",
  "Other organization members will not be able to see this project":
    "Inne członkowie organizacji nie będą mogli widzieć tego projektu",
  "No universal documents available":
    "Brak dostępnych dokumentów uniwersalnych",
  page: "strona",
  pages: "stron",
  "No organization documents available":
    "Brak dostępnych dokumentów organizacji",

  // Universal document picker
  "Choose documents from your library. Hover over items to preview content.":
    "Wybierz dokumenty z biblioteki. Najedź na elementy, aby zobaczyć podgląd treści.",
  "Choose documents from your organization. Hover over items to preview content.":
    "Wybierz dokumenty z organizacji. Najedź na elementy, aby zobaczyć podgląd treści.",
  document: "dokument",
  "No documents available": "Brak dostępnych dokumentów",
  "Select or hover over a document to preview":
    "Wybierz lub najedź na dokument, aby zobaczyć podgląd",
  Cancel: "Anuluj",
  Done: "Gotowe",
  selected: "wybrano",

  // Project creation
  "Projects allow you to attach & organize your own documents and collaborate with your team.":
    "Projekty pozwalają na załączanie i organizowanie własnych dokumentów oraz współpracę z zespołem.",

  // Document selection error messages
  "Failed to update universal document selection. Please try again.":
    "Nie udało się zaktualizować wyboru dokumentu uniwersalnego. Spróbuj ponownie.",
  "Failed to update organization document selection. Please try again.":
    "Nie udało się zaktualizować wyboru dokumentu organizacji. Spróbuj ponownie.",

  // Admin Dashboard
  "Admin Dashboard": "Panel administracyjny",
  "Admin Tools": "Administracja",
  Integrations: "Integracje",
  "Organization Stats": "Statystyki organizacji",
  "Organization metrics and insights": "Metryki i statystyki organizacji",
  "Select time period": "Wybierz okres czasowy",
  "Last 24 hours": "Ostatnie 24 godziny",
  "Last 7 days": "Ostatnie 7 dni",
  "Last 30 days": "Ostatnie 30 dni",
  "Current month": "Bieżący miesiąc",
  "Previous month": "Poprzedni miesiąc",
  "Pick a date range": "Wybierz zakres dat",
  "Quick selection": "Szybki wybór",
  Update: "Aktualizuj",
  "Users Active": "Aktywni użytkownicy",
  "Documents Uploaded": "Przesłane dokumenty",
  "Topics Created": "Utworzone tematy",
  "Messages Sent": "Wysłane wiadomości",
  "Users who created projects": "Użytkownicy, którzy utworzyli projekty",
  "New documents added": "Nowe dodane dokumenty",
  "New conversation topics": "Nowe tematy rozmów",
  "Total messages in conversations": "Łączna liczba wiadomości w rozmowach",
  "User messages in conversations": "Wiadomości użytkowników w rozmowach",
  "Organization Overview": "Przegląd organizacji",
  "Key metrics for your organization over the selected time period":
    "Kluczowe metryki organizacji w wybranym okresie czasowym",
  "Time Period": "Okres czasowy",
  "Organization ID": "ID organizacji",
  "Data Last Updated": "Ostatnia aktualizacja danych",
  Dashboard: "Panel",
  "Daily Activity": "Aktywność dzienna",
  "Topics created and messages sent per day":
    "Tematy utworzone i wiadomości wysłane dziennie",
  "Topics created and user messages sent per day":
    "Tematy utworzone i wiadomości użytkowników wysłane dziennie",
  "Most Active Users": "Najbardziej aktywni użytkownicy",
  "Users with the most activity (topics + messages)":
    "Użytkownicy z największą aktywnością (tematy + wiadomości)",
  "Users with the most messages sent":
    "Użytkownicy z największą liczbą wysłanych wiadomości",
  User: "Użytkownik",
  Activity: "Aktywność",
  "Show More": "Pokaż więcej",
  "Show Less": "Pokaż mniej",
  more: "więcej",

  // Model settings
  "Model settings": "Ustawienia modelu",
  Model: "Model",
  Reasoning: "Rozumowanie",
  Verbosity: "Szczegółowość",

  // Reasoning levels
  "reasoning.None": "Szybkie",
  "reasoning.Medium": "Standardowe",
  "reasoning.High": "Dogłębne",

  // Verbosity levels
  "verbosity.Low": "Zwięźle",
  "verbosity.Medium": "Normalnie",
  "verbosity.High": "Szczegółowo",

  // Reasoning effort descriptions
  "reasoning.none": "Błyskawiczne odpowiedzi",
  "reasoning.medium": "Dlugie przetwarzanie, zbalansowane rozumowanie",
  "reasoning.high": "Inteligencja klasy badawczej",

  // Verbosity descriptions
  "verbosity.low": "Zwięzłe odpowiedzi",
  "verbosity.medium": "Umiarkowane wyjaśnienia",
  "verbosity.high": "Szczegółowe i dokładne omówienia",

  // Model selection
  "model.gpt-5": "GPT-5",
  "model.gpt-5.description": "Najnowszy model",

  // Experimental model notice
  "Experimental Model": "Model eksperymentalny",
  "You're using our new experimental AI model. Your feedback helps us improve!":
    "Używasz naszego nowego eksperymentalnego modelu AI. Twoja opinia pomaga nam go ulepszać!",

  // Web browsing
  "Web browsing": "Przeglądanie internetu",
  "Enable web browsing to search the internet":
    "Włącz przeglądanie internetu, aby przeszukać sieć",
  "Searching the web...": "Przeszukuję internet...",
  "Web search complete": "Przeszukano internet",
  "Reading web results": "Czytam wyniki z internetu",

  // Google Drive
  "Google Drive": "Google Drive",
  "Access your Google Drive folders and files":
    "Uzyskaj dostęp do folderów i plików Google Drive",
  Connect: "Połącz",
  Connected: "Połączono",
  Disconnect: "Rozłącz",
  "Are you sure you want to disconnect Google Drive?":
    "Czy na pewno chcesz rozłączyć Google Drive?",
  "AI can search your Google Drive files":
    "AI może przeszukiwać pliki na Google Drive",
  "Searching Google Drive...": "Przeszukuję Google Drive...",
  "Google Drive search complete": "Przeszukano Google Drive",
  "Opening file from Google Drive...": "Otwieram plik z Google Drive...",
  "File opened successfully": "Plik otwarty pomyślnie",
  "Failed to open file": "Nie udało się otworzyć pliku",
  "Open in Google Docs": "Otwórz w Google Docs",
  "Open in Google Sheets": "Otwórz w Google Sheets",
  Found: "Znaleziono",
  files: "plików",
  "No files found in Google Drive": "Nie znaleziono plików w Google Drive",
  "Google Drive (entire)": "Google Drive (cały)",
  "Include entire Google Drive": "Uwzględnij cały Google Drive",
  "Search across all accessible files (My Drive + Shared Drives).":
    "Przeszukuj wszystkie dostępne pliki (Mój dysk + dyski współdzielone).",
  "Whole drive enabled": "Włączono cały dysk",
  "Disable whole-drive search to attach folders or files.":
    "Wyłącz przeszukiwanie całego dysku, aby załączać foldery lub pliki.",

  // OneDrive
  OneDrive: "OneDrive",
  "Are you sure you want to disconnect OneDrive?":
    "Czy na pewno chcesz rozłączyć OneDrive?",
  "AI can search your OneDrive files": "AI może przeszukiwać pliki na OneDrive",
  "Connect OneDrive to attach folders": "Połącz OneDrive, aby załączyć foldery",
  "Your OneDrive connection has expired.":
    "Twoje połączenie z OneDrive wygasło.",
  "Searching OneDrive...": "Szukam w OneDrive...",
  "OneDrive search complete": "Wyszukiwanie w OneDrive zakończone",
  "No files found in OneDrive": "Nie znaleziono plików w OneDrive",
  "Opening file from OneDrive...": "Otwieram plik z OneDrive...",

  // Prompt Improvement
  "Improve prompt": "Popraw prompt",
  "Improving prompt...": "Przepisuję...",
  "Rewrite your question to be clearer and more effective":
    "Przepisz pytanie, aby było jaśniejsze i bardziej efektywne",

  // Read document pages tool
  "Reading document pages...": "Czytam strony dokumentu...",
  "Document pages loaded": "Załadowano strony dokumentu",
  Pages: "Strony",

  // Dropbox
  Dropbox: "Dropbox",
  "Access your Dropbox folders and files":
    "Uzyskaj dostęp do folderów i plików Dropbox",

  // Connected Sources
  "Connected Sources": "Połączone źródła",

  // Cloud Drives
  "Cloud Drives": "Dyski w chmurze",
  "Connect Google Drive to attach folders":
    "Połącz Google Drive, aby załączyć foldery",
  "More providers coming soon": "Więcej dostawców wkrótce",
  "Failed to update folder attachment. Please try again.":
    "Nie udało się zaktualizować załączenia folderu. Spróbuj ponownie.",
  "Limit reached": "Osiągnięto limit",
  "You can attach up to 10 folders or files. Please detach some items first.":
    "Możesz załączyć maksymalnie 10 folderów lub plików. Najpierw odłącz niektóre elementy.",
  "Coming soon": "Wkrótce",
  "Dropbox integration coming soon": "Integracja z Dropbox wkrótce",

  // Google OAuth Settings
  "Google OAuth Configuration": "Konfiguracja Google OAuth",
  "Configure organization-specific Google OAuth credentials for Google Drive integration.":
    "Skonfiguruj dane OAuth Google dla integracji z Google Drive dla organizacji.",
  "✓ Using organization-specific credentials":
    "✓ Używasz danych specyficznych dla organizacji",
  "Google OAuth Client ID": "ID Klienta Google OAuth",
  "Enter your Google OAuth Client ID": "Wprowadź ID Klienta Google OAuth",
  "Currently using organization-specific Client ID":
    "Obecnie używasz ID Klienta specyficznego dla organizacji",
  "Currently using environment variable fallback":
    "Obecnie używasz wartości domyślnej ze zmiennych środowiskowych",
  "Google OAuth Client Secret": "Tajny klucz klienta Google OAuth",
  "Enter your Google OAuth Client Secret":
    "Wprowadź tajny klucz klienta Google OAuth",
  "Leave empty to keep current secret unchanged":
    "Zostaw puste, aby zachować obecny tajny klucz",
  "Save OAuth Settings": "Zapisz ustawienia OAuth",
  "Clear Custom Settings": "Wyczyść niestandardowe ustawienia",
  "Setup Instructions": "Instrukcje konfiguracji",
  "Organization-specific OAuth credentials allow different organizations to use their own Google Cloud projects for Drive integration.":
    "Dane OAuth specyficzne dla organizacji pozwalają różnym organizacjom używać własnych projektów Google Cloud do integracji z Drive.",
  "and create a new project": "i utwórz nowy projekt",
  "and create OAuth 2.0 Client ID (Web application type)":
    "i utwórz OAuth 2.0 Client ID (typ Aplikacja webowa)",
  "Add authorized redirect URI:": "Dodaj autoryzowany URI przekierowania:",
  "Not configured in environment": "Nie skonfigurowano w środowisku",
  "Copy the Client ID and Client Secret to the form above":
    "Skopiuj ID Klienta i Tajny klucz klienta do formularza powyżej",
  "Connect Google Drive using the organization's credentials":
    "Połącz Google Drive używając danych organizacji",
  "Important:": "Ważne:",
  "If Google Drive is already connected with default credentials, you must disconnect and reconnect after setting custom OAuth credentials.":
    "Jeśli Google Drive jest już połączony z domyślnymi danymi, musisz odłączyć i ponownie połączyć po ustawieniu niestandardowych danych OAuth.",

  // Microsoft OAuth Settings (OneDrive)
  "OneDrive Settings": "Ustawienia OneDrive",
  "Microsoft OAuth Configuration": "Konfiguracja Microsoft OAuth",
  "Configure organization-specific Microsoft OAuth credentials for OneDrive integration.":
    "Skonfiguruj dane Microsoft OAuth dla integracji z OneDrive dla organizacji.",
  "OneDrive OAuth configured": "OneDrive OAuth skonfigurowane",
  "Configuration required": "Wymagana konfiguracja",
  "Microsoft OAuth Client ID": "ID klienta Microsoft OAuth",
  "Enter your Microsoft OAuth Client ID": "Wprowadź ID klienta Microsoft OAuth",
  "Microsoft OAuth Client Secret": "Tajny klucz klienta Microsoft OAuth",
  "Enter your Microsoft OAuth Client Secret":
    "Wprowadź tajny klucz klienta Microsoft OAuth",
  "Use this exact URI in your Microsoft app registration":
    "Użyj dokładnie tego URI w rejestracji aplikacji Microsoft",
  "Saved URI differs from current environment:":
    "Zapisany URI różni się od obecnego środowiska:",
  "Both Client ID and Client Secret are required":
    "Wymagane są zarówno ID klienta, jak i tajny klucz klienta",
  "Failed to save OAuth credentials": "Nie udało się zapisać danych OAuth",
  "Microsoft OAuth credentials saved successfully":
    "Dane Microsoft OAuth zapisane pomyślnie",
  "Failed to save credentials": "Nie udało się zapisać danych",
  "Redirect URI:": "Redirect URI:",
  Success: "Sukces",

  // Organization settings
  Organizations: "Organizacje",
  "Configure AI model settings for each organization":
    "Konfiguruj ustawienia modelu AI dla każdej organizacji",
  "No organizations found": "Nie znaleziono organizacji",
  "Model updated": "Model zaktualizowany",
  "Organization model has been updated successfully.":
    "Model organizacji został pomyślnie zaktualizowany.",
  "Failed to update organization model.":
    "Nie udało się zaktualizować modelu organizacji.",
  "Gemini 3 Pro": "Gemini 3 Pro",
  Custom: "Niestandardowy",
  Default: "Domyślny",
  "Saving...": "Zapisywanie...",
  Status: "Status",
  member: "członek",
  members: "członków",
  pending: "oczekujące",
  "No members": "Brak członków",
  Unknown: "Nieznany",
  "Pending invitations": "Oczekujące zaproszenia",
  Invite: "Zaproś",
  "Invite user": "Zaproś użytkownika",
  "Send an invitation to join": "Wyślij zaproszenie do",
  "Email address": "Adres email",
  Role: "Rola",
  Member: "Członek",
  Members: "Członkowie",
  Users: "Użytkownicy",
  "Drive credentials": "Dane dostępu do dysków",
  "Google Drive credentials": "Dane Google Drive",
  "OneDrive credentials": "Dane OneDrive",
  "Google Drive credentials are configured for this organization.":
    "Dane Google Drive są skonfigurowane dla tej organizacji.",
  "Google Drive credentials are not configured. Configure them in /app/google-oauth.":
    "Dane Google Drive nie są skonfigurowane. Skonfiguruj je w /app/google-oauth.",
  "OneDrive credentials are configured for this organization.":
    "Dane OneDrive są skonfigurowane dla tej organizacji.",
  "OneDrive credentials are not configured. Configure them in /app/microsoft-oauth.":
    "Dane OneDrive nie są skonfigurowane. Skonfiguruj je w /app/microsoft-oauth.",
  "Messages (30d)": "Wiadomości (30d)",
  Admin: "Administrator",
  "Send invitation": "Wyślij zaproszenie",
  "Sending...": "Wysyłanie...",
  "Invitation sent": "Zaproszenie wysłane",
  "New organization": "Nowa organizacja",
  "Organization created": "Organizacja utworzona",
  "The organization has been created successfully.":
    "Organizacja została pomyślnie utworzona.",
  "Access Revoked": "Okres próbny dobiegł końca",
  "Your organization's trial access has been revoked. Please contact us to discuss further details and continue using the platform.":
    "Okres próbny Twojej organizacji dobiegł końca. Skontaktuj się z nami, aby omówić szczegóły i kontynuować korzystanie z platformy.",
  "Contact Us": "Skontaktuj się z nami",
  "Access status updated": "Status dostępu zaktualizowany",
  "Organization trial access status has been updated.":
    "Status dostępu próbnego organizacji został zaktualizowany.",
  "Create a new organization": "Utwórz nową organizację",
  "Organization name": "Nazwa organizacji",
  "Enter organization name": "Wprowadź nazwę organizacji",
  "Verified domain": "Zweryfikowana domena",
  "Enter verified domain (e.g. example.com)":
    "Wpisz zweryfikowaną domenę (np. example.com)",
  "Users signing up with this email domain will be invited automatically.":
    "Użytkownicy rejestrujący się z tym adresem email otrzymają automatyczne zaproszenie.",
  "Creating...": "Tworzenie...",
  "Create organization": "Utwórz organizację",
  "messages ({{period}})": "wiadomości ({{period}})",
  "The invitation has been sent successfully.":
    "Zaproszenie zostało pomyślnie wysłane.",
  "An error occurred.": "Wystąpił błąd.",
  "Msg/User Limit": "Limit wiad./użytk.",
  "No limit": "Bez limitu",
  "Limit updated": "Limit zaktualizowany",
  "Messages per user limit has been updated successfully.":
    "Limit wiadomości na użytkownika został pomyślnie zaktualizowany.",
  // Transcriptions
  Transcriptions: "Transkrypcje",
  "Transcribe audio files": "Transkrybuj pliki audio",
  "New Transcription": "Nowa transkrypcja",
  "Upload Audio": "Prześlij audio",
  "No transcriptions yet": "Brak transkrypcji",
  "Upload your first audio file to get started":
    "Prześlij pierwszy plik audio, aby rozpocząć",
  "Transcription Details": "Szczegóły transkrypcji",
  "Delete Transcription": "Usuń transkrypcję",
  "Are you sure you want to delete this transcription?":
    "Czy na pewno chcesz usunąć tę transkrypcję?",
  "Transcription deleted": "Transkrypcja usunięta",
  "The transcription has been deleted successfully":
    "Transkrypcja została pomyślnie usunięta",
  "Failed to delete transcription": "Nie udało się usunąć transkrypcji",
  "Transcription uploaded": "Transkrypcja przesłana",
  "Your audio file is being processed": "Twój plik audio jest przetwarzany",
  "Processing transcription...": "Przetwarzanie transkrypcji...",
  "Transcription processed": "Transkrypcja przetworzona",
  "Transcription failed": "Transkrypcja nie powiodła się",
  Transcript: "Transkrypt",
  "Audio file": "Plik audio",
  "Supported formats: MP3, WAV":
    "Obsługiwane formaty: MP3, MP4, M4A, WAV, FLAC, OGG, WEBM",
  "Unsupported audio format":
    "{{filename}} nie jest obsługiwanym formatem audio. Obsługiwane: MP3, MP4, M4A, WAV, FLAC, OGG, WEBM.",
  "Convert to document": "Konwertuj na dokument",
  "Make this transcript searchable in your projects":
    "Uczyń transkrypt przeszukiwalnym w projektach",
  "Converted {{time}}": "Skonwertowano {{time}}",
  "Converting...": "Konwertowanie...",
  "Already converted": "Już skonwertowano",
  "Transcription converted to document":
    "Transkrypcja skonwertowana na dokument",
  Joined: "Dołączył/a",
  "Last active": "Ostatnio aktywny/a",
  Never: "Nigdy",

  // Leaflet
  "Leaflet.metaTitle": "Ulotka AnyLawyer",
  "Leaflet.metaDescription":
    "Ulotka A4 do druku opisująca AnyLawyer oraz cennik.",
  "Leaflet.logoAlt": "Logo AnyLawyer",
  "Leaflet.toolbarHint": "Podgląd A4 — otwórz okno drukowania, by zapisać PDF.",
  "Leaflet.print": "Drukuj",
  "Leaflet.badge": "Ulotka A4 do druku",
  "Leaflet.title": "Legal Intelligence, Perfected.",
  "Leaflet.subtitle":
    "Bezpieczna przestrzeń AI do analizy dokumentów prawnych. Analizuj tysiące dokumentów w kilka minut. Integracja z Google Drive, OneDrive i prywatną chmurą. Polityka zero-treningu oraz odpowiedzi z cytatami opartymi o źródła.",
  "Leaflet.contactLabel": "Kontakt",
  "Leaflet.trialLabel": "Okres próbny",
  "Leaflet.trialValue": "30 dni za darmo",
  "Leaflet.whatIsTitle": "Czym jest AnyLawyer?",
  "Leaflet.whatIsBody":
    "Wgraj umowy, akta sądowe lub regulacje. Zadawaj pytania w naturalnym języku. Otrzymuj odpowiedzi z klikalnymi cytatami do konkretnych stron i paragrafów.",
  "Leaflet.aiAndLawyerTitle": "AI + prawnik",
  "Leaflet.aiCardTitle": "AI",
  "Leaflet.aiCardBody":
    "Automatyzuje przegląd, odpowiada na pytania i tworzy raporty—zawsze z oparciem o dowody.",
  "Leaflet.lawyerCardTitle": "Prawnik",
  "Leaflet.lawyerCardBody":
    "Zachowuje kontrolę: weryfikuje źródła, sprawdza tok rozumowania i doradza z pewnością.",
  "Leaflet.groundedTitle": "Cytaty osadzone w źródłach",
  "Leaflet.groundedBody":
    "Każde stwierdzenie jest podlinkowane do źródłowego fragmentu do natychmiastowej weryfikacji. Zero czarnej skrzynki.",
  "Leaflet.securityTitle": "Bezpieczeństwo i suwerenność danych",
  "Leaflet.security1":
    "Szyfrowanie end-to-end danych w tranzycie oraz w spoczynku",
  "Leaflet.security2": "Ścisła izolacja projektów i danych",
  "Leaflet.security3":
    "Polityka zero-treningu: nigdy nie używamy Twoich danych do trenowania modeli",
  "Leaflet.security4": "Dostępne wdrożenia w prywatnej chmurze",
  "Leaflet.capabilitiesTitle": "Najważniejsze możliwości",
  "Leaflet.cap1Title": "Automatyczne due diligence",
  "Leaflet.cap1Body":
    "Wyszukuj i wyciągaj kluczowe postanowienia ze setek dokumentów z dokładnością wspartą cytatami.",
  "Leaflet.cap2Title": "Natychmiastowy research sprawy",
  "Leaflet.cap2Body":
    "Zadawaj złożone pytania całemu plikowi sprawy i otrzymuj odpowiedzi zakorzenione w dowodach.",
  "Leaflet.cap3Title": "Generowanie dokumentów",
  "Leaflet.cap3Body":
    "Twórz notatki prawne i raporty dla klienta bezpośrednio na podstawie dokumentów sprawy.",
  "Leaflet.cap4Title": "Analiza zeznań i protokołów",
  "Leaflet.cap4Body":
    "Przetwarzaj transkrypty i wykrywaj niespójności, kluczowe przyznania oraz wzorce argumentacji.",
  "Leaflet.cap5Title": "Cytaty osadzone w źródłach",
  "Leaflet.cap5Body":
    "Każde stwierdzenie jest powiązane z tekstem źródłowym do natychmiastowej weryfikacji.",
  "Leaflet.cap6Title": "Przejrzyste rozumowanie",
  "Leaflet.cap6Body":
    "Prześledź tok rozumowania AI krok po kroku zanim doradzisz klientowi.",
  "Leaflet.cap7Title": "Integracja ze źródłami prawa",
  "Leaflet.cap7Body":
    "Przeszukuj orzeczenia sądów, interpretacje podatkowe i akty prawne z oficjalnych baz danych.",
  "Leaflet.pricingTitle": "Cennik",
  "Leaflet.pricingSubtitle":
    "Prosty, transparentny cennik. Bez ukrytych opłat i bez długich zobowiązań.",
  "Leaflet.proTitle": "Professional",
  "Leaflet.proPrice": "49€",
  "Leaflet.proPriceSuffix": "miesięcznie, za użytkownika",
  "Leaflet.pro1": "Nielimitowane zapytania",
  "Leaflet.pro2": "Nielimitowane przestrzenie projektowe",
  "Leaflet.pro3": "Dostęp w chmurze SaaS (hostowanie w UE)",
  "Leaflet.pro4": "Odpowiedzi z cytatami",
  "Leaflet.pro5": "Dostrojone modele dla prawa",
  "Leaflet.entTitle": "Enterprise",
  "Leaflet.entPrice": "Indywidualnie",
  "Leaflet.entPriceSuffix": "skontaktuj się po wycenę",
  "Leaflet.ent1": "On-prem / Bring Your Own Cloud",
  "Leaflet.ent2": "SSO (Okta, AD) i zaawansowane IAM",
  "Leaflet.ent3": "Priorytetowe wsparcie 24/7",
  "Leaflet.ent4": "Niestandardowe SLA i umowy",
  "Leaflet.footerTitle": "Umów demo",
  "Leaflet.footerBody":
    "Napisz do nas, aby zobaczyć AnyLawyer na Twoich dokumentach i procesach.",
  "Leaflet.tablePlan": "Plan",
  "Leaflet.tableFree": "Free (14 dni)",
  "Leaflet.tablePlus": "Plus",
  "Leaflet.tablePro": "Pro",
  "Leaflet.tablePrice": "Koszt za stanowisko/miesiąc",
  "Leaflet.tablePlusPrice": "199zł/stanowisko",
  "Leaflet.tableProPrice": "499zł/stanowisko",
  "Leaflet.tableDocuments": "Dokumenty",
  "Leaflet.tableUnlimited": "Nieograniczone",
  "Leaflet.tableMessages": "Wiadomości miesięcznie",
  "Leaflet.tableExtras": "Dodatkowo",
  "Leaflet.tableProExtras": "Transkrypcje, Google Drive/OneDrive",

  // Pricing page
  "Pricing.metaTitle": "Cennik - AnyLawyer",
  "Pricing.metaDescription": "Porównaj plany i cennik AnyLawyer.",
  "Pricing.logoAlt": "Logo AnyLawyer",
  "Pricing.pageTitle": "Cennik",
  "Pricing.saveAsPDF": "Zapisz jako PDF",
  "Pricing.headline": "Wybierz odpowiedni plan dla swojej firmy",
  "Pricing.subheadline":
    "Wszystkie plany obejmują 30-dniowy bezpłatny okres próbny. Karta kredytowa nie jest wymagana.",
  "Pricing.features": "Funkcje",
  "Pricing.planFree": "Free",
  "Pricing.planPlus": "Plus",
  "Pricing.planPro": "Pro",
  "Pricing.planEnterprise": "Enterprise",
  "Pricing.trialPeriod": "30 dni próbnych",
  "Pricing.perSeatMonth": "za stanowisko/miesiąc",
  "Pricing.free": "Bezpłatnie",
  "Pricing.custom": "Indywidualnie",
  "Pricing.monthly": "Miesięcznie",
  "Pricing.annual": "Rocznie",
  "Pricing.poc": "POC dostępny",
  "Pricing.unlimited": "Nieograniczone",
  "Pricing.basic": "Podstawowe",
  "Pricing.advanced": "Zaawansowane",
  "Pricing.allModels": "Wszystkie modele",
  "Pricing.allModelsBYOK": "Wszystkie + BYOK",
  "Pricing.fast": "Szybki",
  "Pricing.fastStandard": "Szybki + Standardowy",
  "Pricing.allModes": "Wszystkie tryby",
  "Pricing.euOrCustom": "UE lub własne",
  "Pricing.community": "Społeczność",
  "Pricing.email": "E-mail",
  "Pricing.priorityEmail": "Priorytetowy e-mail",
  "Pricing.dedicated247": "Dedykowane 24/7",
  "Pricing.contactUs": "Skontaktuj się",
  "Pricing.categoryBasics": "Podstawowe",
  "Pricing.categoryLimits": "Limity użycia",
  "Pricing.categoryAI": "AI i modele",
  "Pricing.categoryFeatures": "Funkcje",
  "Pricing.categoryIntegrations": "Integracje",
  "Pricing.categorySecurity": "Bezpieczeństwo i zgodność",
  "Pricing.categorySupport": "Wsparcie",
  "Pricing.featurePrice": "Cena za stanowisko",
  "Pricing.featureBilling": "Rozliczenie",
  "Pricing.featureTrialDays": "Okres próbny (dni)",
  "Pricing.featureSeats": "Stanowiska",
  "Pricing.featureMessagesMonth": "Wiadomości miesięcznie",
  "Pricing.featureDocuments": "Dokumenty",
  "Pricing.featureProjects": "Projekty",
  "Pricing.featureStoragePerSeat": "Przestrzeń na stanowisko",
  "Pricing.featureMaxFileSize": "Maks. rozmiar pliku",
  "Pricing.featureAIModels": "Modele AI",
  "Pricing.featureReasoningModes": "Tryby rozumowania",
  "Pricing.featureCitations": "Cytaty ze źródeł",
  "Pricing.featureAuditTrail": "Ślad rozumowania AI",
  "Pricing.featureDocumentGeneration": "Generowanie dokumentów",
  "Pricing.featureTranscriptions": "Transkrypcje audio",
  "Pricing.featureDocxExport": "Eksport do DOCX",
  "Pricing.featureGoogleDrive": "Integracja z Google Drive",
  "Pricing.featureOneDrive": "Integracja z OneDrive",
  "Pricing.featureLegalSources": "Polskie źródła prawa",
  "Pricing.featureWebSearch": "Wyszukiwanie w internecie",
  "Pricing.featureAPI": "Dostęp do API",
  "Pricing.featureEncryption": "Szyfrowanie end-to-end",
  "Pricing.featureDataResidency": "Lokalizacja danych",
  "Pricing.featureZeroTraining": "Polityka zero-treningu",
  "Pricing.featureSSO": "SSO (Okta, Azure AD)",
  "Pricing.featureAdvancedIAM": "Zaawansowane IAM",
  "Pricing.featureAuditLogs": "Logi audytowe",
  "Pricing.featurePrivateCloud": "Wdrożenie w prywatnej chmurze",
  "Pricing.featureBYOC": "Bring Your Own Cloud",
  "Pricing.featureSupportLevel": "Wsparcie",
  "Pricing.featureOnboarding": "Sesja onboardingowa",
  "Pricing.featureAccountManager": "Dedykowany opiekun klienta",
  "Pricing.featureCustomSLA": "Niestandardowe SLA",
  "Pricing.featureCustomContract": "Niestandardowa umowa",
  "Pricing.ctaText": "Gotowy do rozpoczęcia?",
  "Pricing.ctaStartTrial": "Rozpocznij okres próbny",
  "Pricing.ctaContactSales": "Skontaktuj się z nami",
  "Pricing.legalSourcesFootnote":
    "obejmują: wyszukiwania w polskich aktach prawnych (ustawy, rozporządzenia), interpretacje podatkowe, orzeczenia sądów powszechnych i Sądu Najwyższego, orzeczenia Trybunału Konstytucyjnego oraz orzeczenia KIO (zamówienia publiczne).",
  "Pricing.footerNote": "Ceny podane w PLN. VAT nie jest wliczony.",

  // Landing page - Navigation
  "Landing.nav.features": "Funkcje",
  "Landing.nav.security": "Bezpieczeństwo",
  "Landing.nav.pricing": "Cennik",
  "Landing.nav.testimonials": "Opinie",
  "Landing.nav.faq": "FAQ",
  "Landing.nav.login": "Zaloguj się",
  "Landing.nav.startTrial": "Rozpocznij za darmo",
  "Landing.nav.goToApp": "Przejdź do AnyLawyer",

  // Landing page - Hero section
  "Landing.hero.badge":
    "Nowość: Regulowana głębokość analizy i mocy rozumowania",
  "Landing.hero.title1": "Legal Intelligence,",
  "Landing.hero.title2": "Perfected.",
  "Landing.hero.description":
    "Wgraj umowy, akta sądowe lub regulacje — albo połącz się z Google Drive lub OneDrive. Zadawaj pytania w naturalnym języku. Otrzymuj odpowiedzi z klikalnymi cytatami do konkretnych stron i paragrafów.",
  "Landing.hero.bookDemo": "Umów demo",
  "Landing.hero.feature1": "Modele dostrojone do prawa",
  "Landing.hero.feature2": "Polityka zero-treningu",
  "Landing.hero.feature3": "14 dni za darmo",

  // Landing page - Trusted by section
  "Landing.trustedBy": "Zaufali nam wiodące kancelarie prawne",

  // Landing page - Bento grid features
  "Landing.bento.title": "Zmień swoją firmę prawną",
  "Landing.bento.description":
    "AnyLawyer pomaga prawnikom oszczędzać czas, redukować błędy i uzyskiwać głębsze wnioski z dokumentów.",
  "Landing.bento.dueDiligence.title": "Automatyczne due diligence",
  "Landing.bento.dueDiligence.description":
    "Identyfikuj i wyciągaj kluczowe postanowienia z setek dokumentów natychmiastowo. Automatyzuj przegląd umów, dokumentacji najmu i materiałów dowodowych z dokładnością wspartą cytatami.",
  "Landing.bento.caseResearch.title": "Natychmiastowy research sprawy",
  "Landing.bento.caseResearch.description":
    "Koniec z ręcznym przeszukiwaniem. Zadawaj złożone pytania całemu plikowi sprawy i otrzymuj odpowiedzi zakorzenione w dowodach.",
  "Landing.bento.reportGen.title": "Generowanie dokumentów",
  "Landing.bento.reportGen.description":
    "Generuj dokładne i kompleksowe notatki prawne oraz raporty dla klienta bezpośrednio na podstawie dokumentów sprawy.",
  "Landing.bento.deposition.title": "Analiza zeznań i protokołów",
  "Landing.bento.deposition.description":
    "Przetwarzaj i analizuj transkrypty sądowe, aby wykryć niespójności, kluczowe przyznania i wzorce argumentacji.",
  "Landing.bento.citations.title": "Cytaty osadzone w źródłach",
  "Landing.bento.citations.description":
    "Każde stwierdzenie jest bezpośrednio powiązane z tekstem źródłowym do natychmiastowej weryfikacji. Zero czarnej skrzynki.",
  "Landing.bento.auditTrail.title": "Przejrzyste rozumowanie",
  "Landing.bento.auditTrail.description":
    "Prześledź rozumowanie AI krok po kroku. Zrozum dokładnie, jak osiągnięto wniosek, zanim doradzisz klientowi.",
  "Landing.bento.questions.q1": "Jakie są kluczowe postanowienia w sekcji 3.2?",
  "Landing.bento.questions.q2": "Kiedy wygasa ta umowa?",
  "Landing.bento.questions.q3":
    "Jakie są moje obowiązki wynikające z tej umowy?",
  "Landing.bento.questions.q4":
    "Czy w tym dokumencie jest klauzula o zakazie konkurencji?",
  "Landing.bento.questions.q5": "Jakie są warunki rozwiązania umowy?",
  "Landing.bento.questions.q6": "Kim są zaangażowane strony?",
  "Landing.bento.questions.q7": "Jakie są warunki płatności?",
  "Landing.bento.questions.q8": "Czy są jakieś klauzule odszkodowawcze?",
  "Landing.bento.questions.q9": "Jakie prawo jest właściwe?",
  "Landing.bento.questions.q10": "Jak można zmienić tę umowę?",

  // Landing page - Complete Legal Toolkit section
  "Landing.toolkit.title": "Kompletny zestaw narzędzi prawnych",
  "Landing.toolkit.description":
    "Wszystko, czego potrzebujesz do zarządzania złożonymi procesami prawnymi w jednej bezpiecznej platformie.",
  "Landing.toolkit.dataHeavy.title": "Stworzony dla sporów opartych na danych",
  "Landing.toolkit.dataHeavy.description":
    "Głęboka analiza kontekstu AnyLawyer obsługuje setki tysięcy dokumentów na projekt, umożliwiając pełne odkrywanie całego korpusu.",
  "Landing.toolkit.citations.title": "Cytaty i weryfikacja",
  "Landing.toolkit.citations.description":
    "Wszystkie odpowiedzi są wsparte precyzyjnymi cytatami z oryginalnego dokumentu. Ufaj, ale weryfikuj.",
  "Landing.toolkit.privateTenant.title": "Prywatne wdrożenie",
  "Landing.toolkit.privateTenant.description":
    "Opcjonalne wdrożenie na własnej prywatnej infrastrukturze chmurowej. Wspieramy AWS, GCP i Azure.",
  "Landing.toolkit.multiStep.title": "Wieloetapowe rozumowanie prawne",
  "Landing.toolkit.multiStep.description":
    "Nasze AI rozbija złożone zapytania prawne na logiczne kroki, aby znaleźć odpowiedzi ukryte w setkach plików.",
  "Landing.toolkit.integration.title": "Integracja z ekosystemem",
  "Landing.toolkit.integration.description":
    "Integracja z Google Drive i OneDrive dostępna; SharePoint wkrótce.",
  "Landing.toolkit.dataIsolation.title": "Ścisła izolacja danych",
  "Landing.toolkit.dataIsolation.description":
    "Izolacja na poziomie projektu zapewnia, że dane klientów nigdy się nie krzyżują. Szyfrowane w spoczynku i w tranzycie. Hosting w UE.",
  "Landing.toolkit.export.title": "Eksport do DOCX",
  "Landing.toolkit.export.description":
    "Eksportuj odpowiedzi do formatu DOCX dla łatwego udostępniania i współpracy.",
  "Landing.toolkit.team.title": "Współpraca zespołowa",
  "Landing.toolkit.team.description":
    "Udostępniaj dokumenty, wnioski, prompty i wyniki wyszukiwania swojemu zespołowi. Twórz współpracujące przestrzenie do badania spraw i dzielenia się wiedzą.",
  "Landing.toolkit.comingSoon": "Wkrótce",

  // Landing page - Security section
  "Landing.security.title": "Gotowość enterprise i suwerenność danych",
  "Landing.security.description":
    "Rozumiemy, że poufność klienta jest najważniejsza. Dlatego AnyLawyer jest zbudowany z architekturą bezpieczeństwa na pierwszym miejscu, zaprojektowaną dla nowoczesnej firmy prawnej.",
  "Landing.security.encryption":
    "Szyfrowanie end-to-end danych w tranzycie i w spoczynku",
  "Landing.security.isolation": "Ścisła izolacja projektów i danych",
  "Landing.security.zeroTraining":
    "Polityka zero-treningu: Nigdy nie używamy Twoich danych do trenowania naszych modeli",
  "Landing.security.privateCloud":
    "Dostępne opcje dedykowanego wdrożenia w prywatnej chmurze",
  "Landing.security.encryptionLabel": "Szyfrowanie",
  "Landing.security.dataResidency": "Lokalizacja danych",
  "Landing.security.dataResidencyValue": "UE (zgodność z RODO)",
  "Landing.security.auditTrailLabel": "Przejrzyste rozumowanie",
  "Landing.security.auditTrailValue": "Niezmienne logi",

  // Landing page - Pricing section
  "Landing.pricing.title": "Prosty, transparentny cennik",
  "Landing.pricing.description":
    "Wybierz plan, który najlepiej odpowiada potrzebom Twojej firmy. Bez ukrytych opłat, bez długoterminowych zobowiązań.",
  "Landing.pricing.trialNote":
    "Wszystkie plany obejmują 30-dniowy bezpłatny okres próbny",
  "Landing.pricing.perSeatMonth": "za stanowisko/miesiąc",
  "Landing.pricing.mostPopular": "Najpopularniejszy",

  // Free plan
  "Landing.pricing.free": "Free",
  "Landing.pricing.freePrice": "0 zł",
  "Landing.pricing.freeDesc": "Wypróbuj AnyLawyer bezpłatnie przez 30 dni",
  "Landing.pricing.freeTrial": "30-dniowy okres próbny",

  // Plus plan
  "Landing.pricing.plus": "Plus",
  "Landing.pricing.plusPrice": "199 zł",
  "Landing.pricing.plusDesc":
    "Dla indywidualnych prawników i małych kancelarii",

  // Pro plan
  "Landing.pricing.pro": "Pro",
  "Landing.pricing.proPrice": "499 zł",
  "Landing.pricing.proDesc":
    "Dla rozwijających się zespołów z zaawansowanymi potrzebami",

  // Enterprise plan
  "Landing.pricing.enterprise": "Enterprise",
  "Landing.pricing.enterprisePrice": "Indywidualnie",
  "Landing.pricing.enterpriseDesc":
    "Kompleksowe rozwiązanie dla dużych organizacji",
  "Landing.pricing.contactForPricing": "skontaktuj się po wycenę",
  "Landing.pricing.contactSales": "Kontakt z nami",

  "Landing.pricing.getStarted": "Rozpocznij",
  "Landing.pricing.startTrial": "Rozpocznij za darmo",

  // Features
  "Landing.pricing.feature.messages50": "50 wiadomości/miesiąc",
  "Landing.pricing.feature.messages500": "500 wiadomości/miesiąc",
  "Landing.pricing.feature.messagesUnlimited": "Nielimitowane wiadomości",
  "Landing.pricing.feature.projects3": "3 projekty",
  "Landing.pricing.feature.projectsUnlimited": "Nielimitowane projekty",
  "Landing.pricing.feature.documentsUnlimited": "Nielimitowane dokumenty",
  "Landing.pricing.feature.basicModels": "Podstawowe modele AI",
  "Landing.pricing.feature.allModels": "Wszystkie modele AI",
  "Landing.pricing.feature.fastReasoning": "Szybki tryb rozumowania",
  "Landing.pricing.feature.allReasoningModes": "Wszystkie tryby rozumowania",
  "Landing.pricing.feature.citations": "Cytaty osadzone w źródłach",
  "Landing.pricing.feature.docxExport": "Eksport do DOCX",
  "Landing.pricing.feature.transcriptions": "Transkrypcje audio",
  "Landing.pricing.feature.googleDrive": "Integracja z Google Drive",
  "Landing.pricing.feature.legalSources": "Polskie źródła prawa",
  "Landing.pricing.feature.webSearch": "Wyszukiwanie w internecie",
  "Landing.pricing.feature.prioritySupport": "Priorytetowe wsparcie",
  "Landing.pricing.feature.onPrem": "Wdrożenie On-prem / BYOC",
  "Landing.pricing.feature.sso": "SSO i zaawansowane IAM",
  "Landing.pricing.feature.customSla": "Niestandardowe SLA i umowy",
  "Landing.pricing.feature.dedicatedManager": "Dedykowany opiekun klienta",
  "Landing.pricing.feature.auditLogs": "Logi audytowe i analityka",
  "Landing.pricing.feature.communitySupport": "Wsparcie społeczności",
  "Landing.pricing.feature.emailSupport": "Wsparcie email",
  "Landing.pricing.feature.priorityEmailSupport": "Priorytetowe wsparcie email",
  "Landing.pricing.feature.dedicated247": "Dedykowane wsparcie 24/7",

  // Landing page - FAQ section
  "Landing.faq.title": "Często",
  "Landing.faq.title2": "zadawane",
  "Landing.faq.title3": "pytania",
  "Landing.faq.subtitle":
    "Wszystko, co musisz wiedzieć o produkcie i rozliczeniach.",
  "Landing.faq.q1": "Jak zapewniacie bezpieczeństwo danych naszych klientów?",
  "Landing.faq.a1":
    "Wdrażamy standardowe branżowe protokoły szyfrowania i utrzymujemy ścisłą izolację danych między dzierżawcami. Twoje dokumenty i informacje nigdy nie są używane do trenowania naszych globalnych modeli, zapewniając pełną poufność i ochronę danych.",
  "Landing.faq.q2": "Jakie integracje są dostępne do zarządzania dokumentami?",
  "Landing.faq.a2":
    "Oferujemy natywną integrację z Google Drive dla bezproblemowej synchronizacji dokumentów. Dodatkowo, integracje z Dropbox i Microsoft SharePoint są dostępne na życzenie, aby dopasować się do istniejącego workflow Twojej firmy.",
  "Landing.faq.q3":
    "Jak wiarygodne są podsumowania i analizy generowane przez AI?",
  "Landing.faq.a3":
    "Nasze modele są specjalnie dostrojone do analizy dokumentów prawnych. Każde podsumowanie zawiera precyzyjne cytaty, które prowadzą bezpośrednio do materiału źródłowego, umożliwiając weryfikację i walidację całej wygenerowanej treści z pewnością.",
  "Landing.faq.q4": "Czy oferujecie ceny enterprise dla większych organizacji?",
  "Landing.faq.a4":
    "Tak, oferujemy indywidualne pakiety cenowe dla firm z 10 lub więcej użytkownikami. Prosimy o kontakt z naszym zespołem sprzedaży, aby omówić rabaty ilościowe i funkcje specyficzne dla przedsiębiorstw dostosowane do potrzeb Twojej organizacji.",
  "Landing.faq.q5": "Gdzie są przechowywane i przetwarzane nasze dane?",
  "Landing.faq.a5":
    "Wszystkie dane są przechowywane i przetwarzane wyłącznie w centrach danych UE, aby zapewnić zgodność z wymogami suwerenności danych. Możemy zapewnić konkretne opcje hostingu regionalnego, aby spełnić regulacje dotyczące rezydencji danych w Twojej jurysdykcji.",
  "Landing.faq.q6": "Jakie umowy o poziomie usług oferujecie?",
  "Landing.faq.a6":
    "Plany Enterprise obejmują SLA 99,9% uptime z priorytetowym wsparciem 24/7 i dedykowanym zarządzaniem kontem. Zapewniamy gwarantowane czasy odpowiedzi, proaktywne monitorowanie i regularne aktualizacje statusu dla zapewnienia ciągłości biznesowej.",
  "Landing.faq.q7": "Jakie są wasze polityki przechowywania i usuwania danych?",
  "Landing.faq.a7":
    "Zachowujesz pełną kontrolę nad politykami przechowywania danych. Przestrzegamy określonych przez Ciebie wymagań dotyczących przechowywania i zapewniamy bezpieczne, certyfikowane usunięcie danych na żądanie lub po rozwiązaniu umowy, zapewniając pełne zarządzanie cyklem życia danych.",
  "Landing.faq.q8": "Czy nasze dane są wykorzystywane do trenowania modeli AI?",
  "Landing.faq.a8":
    "Nie. Dane klienta nie są wykorzystywane do trenowania modeli przez naszych dostawców AI (OpenAI/Google) — dostawca nie może użyć ich do nauki swoich modeli. Dane służą wyłącznie do wygenerowania odpowiedzi w ramach usługi.",

  // Landing page - Final CTA section
  "Landing.cta.title": "Gotowy na modernizację swojej firmy?",
  "Landing.cta.description":
    "Zacznij oszczędzać czas już dziś. Wypróbuj za darmo — bez karty kredytowej.",

  // Landing page - Footer
  "Landing.footer.tagline":
    "Wspieramy profesjonalistów prawnych najnowszymi narzędziami AI.",
  "Landing.footer.product": "Produkt",
  "Landing.footer.legal": "Dokumenty prawne",
  "Landing.footer.privacyPolicy": "Polityka prywatności",
  "Landing.footer.termsOfService": "Regulamin",
  "Landing.footer.contactUs": "Kontakt",
  "Landing.footer.copyright": "© 2025 AnyLawyer. Wszelkie prawa zastrzeżone.",

  // Organization Documents page
  "Manage documents that are available across all projects in your organization":
    "Zarządzaj dokumentami dostępnymi we wszystkich projektach organizacji",
  "Add Documents": "Dodaj dokumenty",
  "Add Organization Documents": "Dodaj dokumenty organizacji",
  "Upload Files": "Prześlij pliki",
  "Choose Files": "Wybierz pliki",
  "You can select multiple files. Max file size: {{size}}MB each.":
    "Możesz wybrać wiele plików. Maksymalny rozmiar pliku: {{size}}MB.",
  "Selected Files ({{count}})": "Wybrane pliki ({{count}})",
  "Document name...": "Nazwa dokumentu...",
  "Upload {{count}} Document": "Prześlij {{count}} dokument",
  "Upload {{count}} Documents": "Prześlij {{count}} dokumenty",
  "Upload Progress": "Postęp przesyłania",
  "{{completed}} / {{total}} completed": "{{completed}} / {{total}} ukończono",
  "No organization documents yet. Add your first one above.":
    "Brak dokumentów organizacji. Dodaj pierwszy dokument powyżej.",
  Name: "Nazwa",
  Created: "Utworzono",
  Actions: "Akcje",
  "uploading {{progress}}%": "przesyłanie {{progress}}%",
  queued: "w kolejce",
  "upload complete": "przesłano",
  "upload failed": "błąd przesyłania",
  "Unsupported file type": "Nieobsługiwany typ pliku",
  "Duplicate file": "Duplikat pliku",
  "{{file}} is already selected.": "{{file}} jest już wybrany.",
  "No files selected": "Nie wybrano plików",
  "Please select at least one file to upload.":
    "Wybierz przynajmniej jeden plik do przesłania.",
  "Missing names": "Brak nazw",
  "Please provide names for all selected documents.":
    "Podaj nazwy dla wszystkich wybranych dokumentów.",
  "Upload started": "Rozpoczęto przesyłanie",
  "Starting upload of {{count}} document(s).":
    "Rozpoczynam przesyłanie {{count}} dokumentów.",
  "Document uploaded": "Dokument przesłany",
  "{{name}} has been uploaded and is being processed.":
    "{{name}} został przesłany i jest przetwarzany.",
  "Upload timeout": "Przekroczono limit czasu",
  "Document upload is taking longer than expected. Please refresh the page to check if it appeared.":
    "Przesyłanie dokumentu trwa dłużej niż oczekiwano. Odśwież stronę, aby sprawdzić, czy się pojawił.",
  "Document processed": "Dokument przetworzony",
  "{{name}} has been successfully processed.":
    "{{name}} został pomyślnie przetworzony.",
  "Document processing failed": "Przetwarzanie dokumentu nie powiodło się",
  "{{name}} failed to process.": "Nie udało się przetworzyć {{name}}.",

  // ELI Legal Acts Browser
  "Legal Acts Database": "Baza aktów prawnych",
  "Browse and search Polish legal acts from the official ELI database":
    "Przeglądaj i wyszukuj polskie akty prawne z oficjalnej bazy ELI",
  "Search acts by title...": "Szukaj aktów po tytule...",
  Search: "Szukaj",
  Clear: "Wyczyść",
  Publishers: "Wydawnictwa",
  "No publishers found. Run the Sejm sync to populate the database.":
    "Nie znaleziono wydawnictw. Uruchom synchronizację Sejmu, aby wypełnić bazę danych.",
  acts: "aktów",
  "total acts": "wszystkich aktów",
  "Legal Acts": "Akty prawne",
  Year: "Rok",
  Position: "Pozycja",
  "All years": "Wszystkie lata",
  Showing: "Wyświetlono",
  Acts: "Akty",
  "Acts from": "Akty z",
  "All acts from this publisher": "Wszystkie akty tego wydawnictwa",
  "No acts found for the selected criteria.":
    "Nie znaleziono aktów spełniających wybrane kryteria.",
  Previous: "Poprzednia",
  of: "z",
  Next: "Następna",
  "Search results for": "Wyniki wyszukiwania dla",
  "No acts found matching your search.":
    "Nie znaleziono aktów pasujących do wyszukiwania.",
  Address: "Adres",
  Date: "Data",
  "In force": "Obowiązujący",
  "Not in force": "Nieobowiązujący",
  Metadata: "Metadane",
  ELI: "ELI",
  Type: "Typ",
  "Announcement Date": "Data ogłoszenia",
  "Entry Into Force": "Wejście w życie",
  "Valid From": "Ważny od",
  Promulgation: "Promulgacja",
  "Expiration Date": "Data wygaśnięcia",
  "Repeal Date": "Data uchylenia",
  "Legal Status Date": "Data statusu prawnego",
  "Change Date": "Data zmiany",
  Volume: "Tom",
  "Authorized Body": "Organ uprawniony",
  Obligated: "Zobowiązani",
  "Released By": "Wydany przez",
  Comments: "Komentarze",
  References: "Odniesienia",
  references: "odniesień",
  "No references found for this act.": "Brak odniesień dla tego aktu.",
  "No ELI ID": "Brak identyfikatora ELI",
  Texts: "Teksty",
  "Text Files": "Pliki tekstowe",
  "Text Versions": "Wersje tekstowe",
  "Full Act PDF": "Pełny akt PDF",
  "Consolidated document": "Dokument skonsolidowany",
  "No text files available for this act.":
    "Brak dostępnych plików tekstowych dla tego aktu.",
  "Consolidated text": "Tekst jednolity",
  "Published text": "Tekst opublikowany",
  "Uniform text": "Tekst ujednolicony",
  "Historical text": "Tekst historyczny",
  "Initial text": "Tekst początkowy",
  Text: "Tekst",
  Directives: "Dyrektywy",
  "EU Directives": "Dyrektywy UE",
  "No EU directives linked to this act.":
    "Brak dyrektyw UE powiązanych z tym aktem.",
  Prints: "Druki",
  "Parliamentary Prints": "Druki sejmowe",
  "No parliamentary prints linked to this act.":
    "Brak druków sejmowych powiązanych z tym aktem.",
  Print: "Druk",
  Term: "Kadencja",
  View: "Zobacz",
  "Full Text": "Pełny tekst",
  "Untitled Act": "Akt bez tytułu",
  Title: "Tytuł",
  "Download PDF": "Pobierz PDF",
  "Official PDF": "Oficjalny PDF",
  "Open in new tab": "Otwórz w nowej karcie",
  "PDF Preview": "Podgląd PDF",
  "Sejm API": "API Sejmu",
  "No full text available for this act.":
    "Pełny tekst nie jest dostępny dla tego aktu.",
  "Full text not available. See PDF preview.":
    "Pełny tekst niedostępny. Zobacz podgląd PDF.",
};
